import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Editor } from "primereact/editor";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { MenuItem } from "primereact/menuitem";
import { RadioButton } from "primereact/radiobutton";
import { Steps } from "primereact/steps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Carousel, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Property } from "../models/property.model";
import { ProposalPropertyWithRoomsDto } from "../models/proposal-detailed.model";
import {
  ProposalCreateFullPayload,
  ProposalPropertyMappingCreateDto,
  RMCClientRoomMappingCreateDto,
  RMCRoomChargeCreateDto,
} from "../models/proposal.model";
import { citiesService } from "../service/cities.service";
import { currencyService } from "../service/currency.service";
import { httpService } from "../service/httpService";
import { propertyService } from "../service/property.service";
import { proposalService } from "../service/proposal.service";
import PageHeader from "./PageHeader";
import ProposalPreview from "./ProposalPreview";
import "./ProposalWizard.css";
import ProposalRoomWrapper from "./Room/ProposalRoomWrapper";

interface ProposalFormState {
  name: string;
  description?: string;
  startDate: Date | null;
  endDate: Date | null;
  referenceCode?: string;
  currencyId?: number | null;
  isShowPrice?: boolean;
  creditPeriod: number;
}

interface CitySelection {
  cityId: number;
  cityName: string;
  properties: Property[];
}

interface PropertyRoomData {
  propertyId: number;
  property: Property;
  selectedRooms: number[];
  roomCharges: Record<number, { amount: number; taxPercentage?: number }>;
  roomComplementaries: Record<number, number[]>;
  isRMCClient: boolean;
}

interface WizardState {
  activeIndex: number;
  completedSteps: boolean[];
}

interface PropertyMappingResult {
  proposalId: number;
  mappedProperties: Array<{
    cityId: number;
    propertyId: number;
    clientPropertyMappingId: number;
  }>;
}

const ProposalWizard: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  // Only consume rmcClientMappingId from route params now; rmcId/clientId (if present) are used only for navigation breadcrumbs/UI, not for logic
  const { rmcClientMappingId: rmcClientMappingIdParam } = useParams<{
    rmcClientMappingId: string;
  }>();
  const rmcIdFromAuth = useSelector((state: any) => state.auth.rmcId);
  const rmcId =
    state?.rmcId || sessionStorage.getItem("rmcId") || rmcIdFromAuth;
  const clientId = state?.clientId; // kept only for navigation/back path context (not used for proposal logic)
  const clientName = state?.clientName;
  const editProposal = state?.editProposal;
  const editMode = state?.editMode || false;
  const viewMode = state?.viewMode || false; // View-only mode
  const location = useLocation();

  // Get cityId from navigation state or sessionStorage
  const cityIdFromState = state?.cityId;
  const cityIdFromStorage = sessionStorage.getItem("clientCityId");
  const initialCityId =
    cityIdFromState || (cityIdFromStorage ? Number(cityIdFromStorage) : null);

  // Track if auto-select has already been performed
  const autoSelectPerformedRef = React.useRef(false);

  // Resolve rmcClientMappingId with layered fallbacks: navigation state -> route param -> editProposal -> sessionStorage (single key)
  const resolvedRmcClientMappingId = (() => {
    const fromState = location.state?.rmcClientMappingId;
    if (fromState) return Number(fromState);
    if (rmcClientMappingIdParam) return Number(rmcClientMappingIdParam);
    // For edit mode, try to extract from editProposal object
    if (editMode && editProposal && editProposal.rmcClientMappingId) {
      return Number(editProposal.rmcClientMappingId);
    }
    // sessionStorage fallback (global key since composite rmcId/clientId no longer used)
    try {
      const stored = sessionStorage.getItem("rmcClientMappingId");
      if (stored) {
        const parsed = Number(stored);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    } catch {
      /* ignore storage errors */
    }
    return undefined;
  })();

  const rmcClientMappingId = resolvedRmcClientMappingId;

  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState>({
    activeIndex: 0,
    completedSteps: [false, false, false, false],
  });
  const proposalMode = viewMode ? "(View)" : editMode ? "(Edit)" : "(Create)";
  const wizardSteps: MenuItem[] = [
    {
      label: "Proposal " + proposalMode,
      icon: "pi pi-file",
    },
    {
      label: "Property Selection",
      icon: "pi pi-home",
    },
    {
      label: "Terms & Conditions",
      icon: "pi pi-file-check",
    },
    {
      label: "Preview & Submit",
      icon: "pi pi-eye",
    },
  ];

  // Cities and properties selection
  const [cities, setCities] = useState<{ id: number; name: string }[]>([]);
  // Id of the committed selection (used to load properties). Do NOT tie this to the input text directly.
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  // Controlled value for the AutoComplete input; can be a string while typing or a city object when selected
  const [cityACValue, setCityACValue] = useState<
    string | { id: number; name: string } | null
  >("");
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [citySelections, setCitySelections] = useState<CitySelection[]>([]);

  // City lazy loading states
  const [displayedCities, setDisplayedCities] = useState<
    { id: number; name: string }[]
  >([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearchDebounce, setCitySearchDebounce] =
    useState<NodeJS.Timeout | null>(null);

  // Property search and pagination states
  const [propertySearchTerm, setPropertySearchTerm] = useState("");
  const [displayedProperties, setDisplayedProperties] = useState<Property[]>(
    []
  );
  const [propertyPage, setPropertyPage] = useState(1);
  const [hasMoreProperties, setHasMoreProperties] = useState(false);
  const PROPERTIES_PER_PAGE = 20;

  // Room management state
  const [proposalId, setProposalId] = useState<number | null>(null);
  const [currentProposalStatus, setCurrentProposalStatus] = useState<
    number | null
  >(null); // Track proposal status locally (0=Draft, 1=Proposed)
  const [propertyMappings, setPropertyMappings] = useState<
    PropertyMappingResult["mappedProperties"]
  >([]);
  const [selectedPropertyForRooms, setSelectedPropertyForRooms] =
    useState<Property | null>(null);
  const [showRoomManagement, setShowRoomManagement] = useState(false);
  const [selectedPropertyForRoomView, setSelectedPropertyForRoomView] =
    useState<Property | null>(null);
  const [showRoomList, setShowRoomList] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // New state for comprehensive room data collection
  const [propertyRoomData, setPropertyRoomData] = useState<
    Record<number, PropertyRoomData>
  >({});
  const [useCreateFullAPI, setUseCreateFullAPI] = useState(true); // Flag to determine which API to use
  // transient ref to hold latest draft edits to avoid losing them during rapid switches
  const roomDraftsRef = React.useRef<
    Record<number, Partial<Omit<PropertyRoomData, "propertyId" | "property">>>
  >({});
  // ref to prevent duplicate proposal loading in edit mode
  const proposalLoadedRef = React.useRef<number | null>(null);

  // Change detection: Store initial snapshot of proposal data
  const initialProposalSnapshot = React.useRef<{
    form: ProposalFormState | null;
    propertyRoomData: Record<number, PropertyRoomData>;
    propertyTerms: Record<number, string>;
    citySelections: CitySelection[];
    selectedProperties: Property[];
  }>({
    form: null,
    propertyRoomData: {},
    propertyTerms: {},
    citySelections: [],
    selectedProperties: [],
  });

  // User role for permission checks
  const userRole = useSelector((s: any) => s.auth.userRole) ?? "";
  const isReadOnly = userRole !== "Administrator";
  // Property detail preview state
  const [previewProperty, setPreviewProperty] = useState<Property | null>(null);
  const [showPropertyPreview, setShowPropertyPreview] = useState(false);

  // Check if proposal is in Draft status (status can be string "Draft" or number 0)
  const isDraftProposal =
    (editMode &&
      editProposal &&
      (editProposal.status === "Draft" ||
        editProposal.status === 0 ||
        editProposal.status === "0" ||
        editProposal.status?.toLowerCase() === "draft")) ||
    currentProposalStatus === 0; // Also check local status for new proposals

  // Check if proposal is in Proposed status (status can be string "Proposed" or number 1)
  const isProposedProposal =
    (editMode &&
      editProposal &&
      (editProposal.status === "Proposed" ||
        editProposal.status === 1 ||
        editProposal.status === "1" ||
        editProposal.status?.toLowerCase() === "proposed")) ||
    currentProposalStatus === 1; // Also check local status for new proposals

  // Check if proposal is in Approved or Expired status (read-only, no updates allowed)
  const isReadOnlyProposal =
    editMode &&
    editProposal &&
    (editProposal.status === "Approved" ||
      editProposal.status === 2 ||
      editProposal.status === "2" ||
      editProposal.status?.toLowerCase() === "approved" ||
      editProposal.status === "Expired" ||
      editProposal.status === 3 ||
      editProposal.status === "3" ||
      editProposal.status?.toLowerCase() === "expired");

  // Proposal form state
  const initialForm: ProposalFormState =
    editMode && editProposal
      ? {
          name: editProposal.name,
          description: editProposal.description || "",
          startDate: editProposal.startDate
            ? new Date(editProposal.startDate)
            : null,
          endDate: editProposal.endDate ? new Date(editProposal.endDate) : null,
          referenceCode: editProposal.ReferenceCode || "",
          currencyId: editProposal.currencyId ?? null,
          creditPeriod: editProposal.creditPeriod ?? 0,
          isShowPrice: editProposal.isShowPrice ?? true,
        }
      : {
          name: "",
          description: "",
          startDate: null,
          endDate: null,
          referenceCode: "",
          currencyId: null,
          isShowPrice: true,
          creditPeriod: 0,
        };
  const [form, setForm] = useState<ProposalFormState>(initialForm);
  const [currencies, setCurrencies] = useState<
    { id: number; name: string; code?: string; symbol?: string }[]
  >([]);

  // Terms & Conditions state - property-wise terms
  const [propertyTerms, setPropertyTerms] = useState<Record<number, string>>(
    {}
  );

  // Track character counts for property terms (HTML length)
  const [propertyTermsCharCount, setPropertyTermsCharCount] = useState<
    Record<number, number>
  >({});
  const MAX_TERMS_LENGTH = 4000;

  const formatDate = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  // Function to create a deep clone snapshot of current state
  const createProposalSnapshot = useCallback(() => {
    return {
      form: JSON.parse(JSON.stringify(form)),
      propertyRoomData: JSON.parse(
        JSON.stringify(
          Object.fromEntries(
            Object.entries(propertyRoomData).map(([key, value]) => [
              key,
              {
                propertyId: value.propertyId,
                selectedRooms: value.selectedRooms,
                roomCharges: value.roomCharges,
                roomComplementaries: value.roomComplementaries,
                isRMCClient: value.isRMCClient,
              },
            ])
          )
        )
      ),
      propertyTerms: JSON.parse(JSON.stringify(propertyTerms)),
      citySelections: JSON.parse(
        JSON.stringify(
          citySelections.map((cs) => ({
            cityId: cs.cityId,
            cityName: cs.cityName,
            properties: cs.properties.map((p) => ({ id: p.id, name: p.name })),
          }))
        )
      ),
      selectedProperties: JSON.parse(
        JSON.stringify(
          selectedProperties.map((p) => ({ id: p.id, name: p.name }))
        )
      ),
    };
  }, [
    form,
    propertyRoomData,
    propertyTerms,
    citySelections,
    selectedProperties,
  ]);

  // Function to detect if there are any changes
  const hasProposalChanges = useCallback((): boolean => {
    const snapshot = initialProposalSnapshot.current;

    // If no snapshot yet, consider it as changed (initial save)
    if (!snapshot.form) {
      return true;
    }

    // Compare form data
    const formChanged = JSON.stringify(form) !== JSON.stringify(snapshot.form);

    // Compare property room data (excluding property objects)
    const currentRoomDataString = JSON.stringify(
      Object.fromEntries(
        Object.entries(propertyRoomData).map(([key, value]) => [
          key,
          {
            propertyId: value.propertyId,
            selectedRooms: value.selectedRooms,
            roomCharges: value.roomCharges,
            roomComplementaries: value.roomComplementaries,
            isRMCClient: value.isRMCClient,
          },
        ])
      )
    );
    const roomDataChanged =
      currentRoomDataString !== JSON.stringify(snapshot.propertyRoomData);

    // Compare property terms
    const termsChanged =
      JSON.stringify(propertyTerms) !== JSON.stringify(snapshot.propertyTerms);

    // Compare city selections and selected properties
    const currentCitySelectionsString = JSON.stringify(
      citySelections.map((cs) => ({
        cityId: cs.cityId,
        cityName: cs.cityName,
        properties: cs.properties.map((p) => ({ id: p.id, name: p.name })),
      }))
    );
    const citySelectionsChanged =
      currentCitySelectionsString !== JSON.stringify(snapshot.citySelections);

    const currentSelectedPropertiesString = JSON.stringify(
      selectedProperties.map((p) => ({ id: p.id, name: p.name }))
    );
    const selectedPropertiesChanged =
      currentSelectedPropertiesString !==
      JSON.stringify(snapshot.selectedProperties);

    const hasChanges =
      formChanged ||
      roomDataChanged ||
      termsChanged ||
      citySelectionsChanged ||
      selectedPropertiesChanged;

    if (hasChanges) {
      console.log("=== Change Detection ===");
      console.log("Form changed:", formChanged);
      console.log("Room data changed:", roomDataChanged);
      console.log("Terms changed:", termsChanged);
      console.log("City selections changed:", citySelectionsChanged);
      console.log("Selected properties changed:", selectedPropertiesChanged);
    }

    return hasChanges;
  }, [
    form,
    propertyRoomData,
    propertyTerms,
    citySelections,
    selectedProperties,
  ]);

  // Helper function to get city name with fallback
  const getCityName = (property: Property): string => {
    // 1. Try property's city object if available (from API)
    if (property.city?.name) return property.city.name;

    // 2. If property's cityId matches the currently selected city, use the selected city name
    if (property.cityId === selectedCityId && selectedCityName) {
      return selectedCityName;
    }

    // 3. Try to find in cities array (from autocomplete search results)
    if (property.cityId) {
      const city = cities.find((c) => c.id === property.cityId);
      if (city) return city.name;
    }

    // 4. Fallback: try to get from propertyRoomData
    const roomData = propertyRoomData[property.id!];
    if (roomData?.property?.city?.name) return roomData.property.city.name;

    return "City Not Specified";
  };

  // Set proposal ID if editing
  useEffect(() => {
    if (editMode && editProposal) {
      setProposalId(editProposal.id);
      // Set the current proposal status from editProposal
      if (
        editProposal.status === "Draft" ||
        editProposal.status === 0 ||
        editProposal.status === "0"
      ) {
        setCurrentProposalStatus(0);
      } else if (
        editProposal.status === "Proposed" ||
        editProposal.status === 1 ||
        editProposal.status === "1"
      ) {
        setCurrentProposalStatus(1);
      } else if (
        editProposal.status === "Approved" ||
        editProposal.status === 2 ||
        editProposal.status === "2"
      ) {
        setCurrentProposalStatus(2);
      } else if (
        editProposal.status === "Expired" ||
        editProposal.status === 3 ||
        editProposal.status === "3"
      ) {
        setCurrentProposalStatus(3);
      }
      // Reset the loaded ref when proposal changes
      proposalLoadedRef.current = null;
    } else {
      // Reset when not in edit mode
      proposalLoadedRef.current = null;
      setCurrentProposalStatus(null);
    }
  }, [editMode, editProposal, rmcClientMappingId]);

  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        let list: any = [];
        try {
          list = await currencyService.getAll();
        } catch {
          list = await currencyService.getListItems();
        }
        if (list && typeof list === "object" && "$values" in list)
          list = list.$values;
        const normalized = (list || []).map((c: any) => ({
          id: Number(c.id ?? c.value ?? c.Value ?? 0),
          name: c.name ?? c.Name ?? String(c),
          // keep code/symbol only when provided by API; do not fallback to name
          code: c.code ?? c.Code ?? undefined,
          symbol: c.symbol ?? c.Symbol ?? undefined,
        }));
        setCurrencies(normalized.filter((c: any) => c && c.id));
        // Currency is now set via initialForm
      } catch (err) {
        setCurrencies([]);
      }
    };
    loadCurrencies();
  }, [editMode, editProposal]);

  // derive selected currency symbol for passing into room components
  const selectedCurrencySymbol = (() => {
    try {
      const found = currencies.find(
        (c) => Number(c.id) === Number(form.currencyId)
      );
      return found?.symbol ?? found?.code ?? "";
    } catch {
      return "";
    }
  })();

  // Persist mapping ID when available so a refresh retains context
  useEffect(() => {
    if (!rmcClientMappingId) return;
    try {
      sessionStorage.setItem("rmcClientMappingId", String(rmcClientMappingId));
    } catch {
      /* storage write can fail silently */
    }
  }, [rmcClientMappingId]);

  // Persist rmcId when available to ensure it's not lost on navigation
  useEffect(() => {
    if (!rmcId) return;
    try {
      sessionStorage.setItem("rmcId", String(rmcId));
    } catch {
      /* storage write can fail silently */
    }
  }, [rmcId]);

  // Load existing proposal details in edit mode
  const loadExistingProposalDetails = useCallback(async () => {
    if (!editMode || !editProposal || !editProposal.id) return;

    // Prevent duplicate loading for the same proposal
    if (proposalLoadedRef.current === editProposal.id) {
      console.log("Proposal details already loaded for ID:", editProposal.id);
      return;
    }

    try {
      console.log("Loading existing proposal details for ID:", editProposal.id);
      proposalLoadedRef.current = editProposal.id; // Mark as loading/loaded
      const detailedProposal = await proposalService.getDetailedProposal(
        editProposal.id
      );

      if (detailedProposal && detailedProposal.properties) {
        let properties = detailedProposal.properties;
        if (!Array.isArray(properties) && properties?.$values) {
          properties = properties.$values;
        }

        if (Array.isArray(properties) && properties.length > 0) {
          // Group properties by city
          const cityGroups: Record<
            number,
            { cityName: string; properties: Property[] }
          > = {};

          for (const propDetail of properties) {
            // Use the cityId directly from the API response
            const cityId = propDetail.cityId;
            const cityName = propDetail.cityName;

            // Check if we already have a city group for this city ID
            if (!cityGroups[cityId]) {
              cityGroups[cityId] = {
                cityName: cityName,
                properties: [],
              };
            }

            // Now create the property with the correct cityId
            const property: Property = {
              id: propDetail.propertyId,
              name: propDetail.propertyName,
              description: "", // Default empty description
              propertyTypeId: propDetail.propertyTypeId,
              address: propDetail.address || "",
              cityId: cityId,
              zipCode: "", // Default empty zip code
              locationId: 0, // Default location ID
              landmark: "", // Default empty landmark
             // creditPeriod: 0, // Default credit period
              totalInventory: 0, // Default inventory
              furnishingType: "", // Default furnishing type
              buildYear: 0, // Default build year
              propertyType: {
                id: propDetail.propertyTypeId,
                name: propDetail.propertyTypeName,
              },
              propertyImages: Array.isArray(propDetail.propertyImages)
                ? propDetail.propertyImages
                : propDetail.propertyImages?.$values || [],
            };

            cityGroups[cityId].properties.push(property);
          }

          // Convert to citySelections format
          const existingCitySelections: CitySelection[] = Object.entries(
            cityGroups
          ).map(([cityId, group]) => ({
            cityId: Number(cityId),
            cityName: group.cityName,
            properties: group.properties,
          }));

          setCitySelections(existingCitySelections);

          // Initialize property room data and extract RMC override values
          if (useCreateFullAPI) {
            existingCitySelections.forEach((citySelection) => {
              citySelection.properties.forEach((property) => {
                if (!propertyRoomData[property.id!]) {
                  // Find the original property detail to extract room data
                  const propDetail = properties.find(
                    (p: ProposalPropertyWithRoomsDto) =>
                      p.propertyId === property.id
                  );
                  if (propDetail && propDetail.rooms) {
                    // Extract room data with RMC overrides
                    const roomData = extractRoomDataFromProposal(propDetail);
                    initializePropertyRoomDataWithExistingData(
                      property,
                      roomData
                    );
                  } else {
                    initializePropertyRoomData(property);
                  }
                }
              });
            });
          }

          // Load existing terms & conditions for properties from API response
          try {
            const termsMap: Record<number, string> = {};
            const charCountMap: Record<number, number> = {};
            for (const propDetail of properties) {
              // Use terms from API response (already loaded with GetDetailedProposal)
              if (propDetail.rmcTerms) {
                termsMap[propDetail.propertyId] = propDetail.rmcTerms;
                charCountMap[propDetail.propertyId] =
                  propDetail.rmcTerms.length;
              }
            }
            setPropertyTerms(termsMap);
            setPropertyTermsCharCount(charCountMap);
            console.log("Loaded terms for properties:", Object.keys(termsMap));
          } catch (termsError) {
            console.error("Error loading property terms:", termsError);
            // Don't fail the whole load, just log the error
          }
        }
      }
    } catch (error) {
      console.error("Error loading existing proposal details:", error);
      // Don't reset proposalLoadedRef on error to prevent infinite retries
      // The ref will be reset when the component unmounts or proposal changes
    }
  }, [editMode, editProposal, useCreateFullAPI]);

  // Helper function to extract room data from proposal details
  const extractRoomDataFromProposal = (propDetail: any) => {
    let rooms = propDetail.rooms;
    if (!Array.isArray(rooms) && rooms?.$values) {
      rooms = rooms.$values;
    }

    const selectedRooms: number[] = [];
    const roomCharges: Record<
      number,
      { amount: number; taxPercentage?: number }
    > = {};
    const roomComplementaries: Record<number, number[]> = {};

    if (Array.isArray(rooms)) {
      rooms.forEach((room: any) => {
        selectedRooms.push(room.roomId);

        // Extract RMC room charges
        let rmcCharges = room.rmcRoomCharges;
        if (!Array.isArray(rmcCharges) && rmcCharges?.$values) {
          rmcCharges = rmcCharges.$values;
        }

        if (Array.isArray(rmcCharges) && rmcCharges.length > 0) {
          const charge = rmcCharges[0]; // Take the first charge
          roomCharges[room.roomId] = {
            amount: charge.amount || 0,
            taxPercentage: charge.taxPercentage,
          };
        }

        // Extract RMC room complementaries
        let rmcComplementaries = room.rmcRoomComplementaries;
        if (!Array.isArray(rmcComplementaries) && rmcComplementaries?.$values) {
          rmcComplementaries = rmcComplementaries.$values;
        }

        if (
          Array.isArray(rmcComplementaries) &&
          rmcComplementaries.length > 0
        ) {
          roomComplementaries[room.roomId] = rmcComplementaries.map(
            (comp: any) => comp.complementaryId
          );
        }
      });
    }

    return { selectedRooms, roomCharges, roomComplementaries };
  };

  // Helper function to initialize property room data with existing data
  const initializePropertyRoomDataWithExistingData = (
    property: Property,
    roomData: any
  ) => {
    setPropertyRoomData((prev) => ({
      ...prev,
      [property.id!]: {
        propertyId: property.id!,
        property: property,
        // clone arrays/objects to avoid shared references
        selectedRooms: Array.isArray(roomData.selectedRooms)
          ? [...roomData.selectedRooms]
          : [],
        roomCharges: roomData.roomCharges
          ? JSON.parse(JSON.stringify(roomData.roomCharges))
          : {},
        roomComplementaries: roomData.roomComplementaries
          ? JSON.parse(JSON.stringify(roomData.roomComplementaries))
          : {},
        isRMCClient: true, // Assume RMC client if we have override data
      },
    }));
  };

  // Load existing proposal details when in edit mode and cities are loaded
  useEffect(() => {
    if (
      editMode &&
      cities.length > 0 &&
      editProposal?.id &&
      proposalLoadedRef.current !== editProposal.id
    ) {
      loadExistingProposalDetails();
    }
  }, [editMode, cities.length, editProposal?.id, useCreateFullAPI]); // Remove function dependency

  // Create initial snapshot after proposal data is loaded (for change detection)
  useEffect(() => {
    if (
      editMode &&
      editProposal?.id &&
      proposalLoadedRef.current === editProposal.id &&
      !initialProposalSnapshot.current.form
    ) {
      // Small delay to ensure all state updates from loadExistingProposalDetails are complete
      const timer = setTimeout(() => {
        initialProposalSnapshot.current = createProposalSnapshot();
        console.log("=== Initial proposal snapshot created ===");
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [
    editMode,
    editProposal?.id,
    proposalLoadedRef.current,
    createProposalSnapshot,
    citySelections.length,
    Object.keys(propertyRoomData).length,
    Object.keys(propertyTerms).length,
  ]);

  const loadCities = useCallback(
    async (searchTerm: string = "") => {
      if (citySearchDebounce) {
        clearTimeout(citySearchDebounce);
      }

      const timeout = setTimeout(async () => {
        setLoadingCities(true);
        try {
          const response = await citiesService.searchCities(
            searchTerm || undefined,
            undefined,
            undefined,
            1,
            50
          );

          const mappedCities = response.items.map((c: any) => ({
            id: Number(c.id),
            name: c.name,
          }));

          // Only update state if data actually changed (prevents unnecessary re-renders and focus loss)
          setDisplayedCities((prev) => {
            const isDifferent =
              prev.length !== mappedCities.length ||
              prev.some((p, i) => p.id !== mappedCities[i]?.id);
            return isDifferent ? mappedCities : prev;
          });

          setCities((prev) => {
            const isDifferent =
              prev.length !== mappedCities.length ||
              prev.some((p, i) => p.id !== mappedCities[i]?.id);
            return isDifferent ? mappedCities : prev;
          });
        } catch (err) {
          console.error("Error loading cities:", err);
          setDisplayedCities([]);
          setCities([]);
        } finally {
          setLoadingCities(false);
        }
      }, 300);

      setCitySearchDebounce(timeout);
    },
    [citySearchDebounce]
  );

  const loadProperties = useCallback(
    async (
      searchTerm: string = "",
      page: number = 1,
      append: boolean = false
    ) => {
      if (selectedCityId == null) {
        setProperties([]);
        setDisplayedProperties([]);
        setSelectedProperties([]);
        setHasMoreProperties(false);
        return;
      }
      setLoadingProperties(true);
      try {
        const props = await propertyService.getPropertiesByCityId(
          Number(selectedCityId)
        );

        // DEBUG: Check if city object is included in API response
        console.log(
          "[DEBUG] Properties from API:",
          props.slice(0, 2).map((p) => ({
            id: p.id,
            name: p.name,
            cityId: p.cityId,
            city: p.city,
            hasCityObject: !!p.city,
            hasCityName: !!p.city?.name,
          }))
        );

        // Filter properties based on search term (client-side filtering)
        let filteredProps = props;
        if (searchTerm.trim()) {
          const lowerSearch = searchTerm.toLowerCase();
          filteredProps = props.filter(
            (p) =>
              p.name?.toLowerCase().includes(lowerSearch) ||
              p.pseudoName?.toLowerCase().includes(lowerSearch) ||
              p.address?.toLowerCase().includes(lowerSearch)
          );
        }

        // Store all properties for reference
        setProperties(props);

        // Implement pagination
        const startIndex = (page - 1) * PROPERTIES_PER_PAGE;
        const endIndex = startIndex + PROPERTIES_PER_PAGE;
        const paginatedProps = filteredProps.slice(startIndex, endIndex);

        // Update displayed properties (append for "Load More" or replace for new search)
        if (append) {
          setDisplayedProperties((prev) => [...prev, ...paginatedProps]);
        } else {
          setDisplayedProperties(paginatedProps);

          // Automatically show the first property's rooms when properties are loaded
          if (paginatedProps.length > 0 && !append) {
            const firstProperty = paginatedProps[0];
            console.log(
              "Auto-opening rooms for first property:",
              firstProperty.name
            );
            setSelectedPropertyForRoomView(firstProperty);
            setShowRoomList(true);
          }
        }

        // Check if there are more properties to load
        setHasMoreProperties(endIndex < filteredProps.length);
        setPropertyPage(page);
      } catch (e) {
        console.error("Failed loading properties", e);
        setDisplayedProperties([]);
        setHasMoreProperties(false);
      } finally {
        setLoadingProperties(false);
      }
    },
    [selectedCityId, PROPERTIES_PER_PAGE]
  );

  // City search handler for AutoComplete
  const searchCities = (event: { query: string }) => {
    const query = event.query || "";
    loadCities(query);
  };

  useEffect(() => {
    loadCities("");
  }, []);

  // Keep the AutoComplete input value in sync when selection changes
  // Only sync when selectedCityId changes, not when cities array changes (to avoid clearing user input)
  useEffect(() => {
    if (selectedCityId) {
      const sel = cities.find((c) => c.id === selectedCityId);
      if (sel) {
        setCityACValue(sel);
      }
    } else if (selectedCityId === null && typeof cityACValue === "object") {
      // Only clear if we explicitly deselected and the value is an object (selected item)
      setCityACValue("");
    }
  }, [selectedCityId]); // Removed 'cities' from dependencies

  // Auto-select city if cityId is provided and cities are loaded (only for create mode, not edit)
  // This effect only runs once when the component mounts with initialCityId
  useEffect(() => {
    if (
      !editMode &&
      initialCityId &&
      cities.length > 0 &&
      !autoSelectPerformedRef.current
    ) {
      const cityExists = cities.find((c) => c.id === Number(initialCityId));
      if (cityExists) {
        console.log(`Auto-selecting city with ID: ${initialCityId}`);
        setSelectedCityId(Number(initialCityId));
        autoSelectPerformedRef.current = true; // Mark as performed
      } else {
        console.warn(
          `City with ID ${initialCityId} not found in the cities list`
        );
        autoSelectPerformedRef.current = true; // Mark as performed even if city not found
      }
    }
  }, [initialCityId, cities, editMode]);

  // Load properties when city changes
  useEffect(() => {
    if (selectedCityId) {
      setPropertySearchTerm(""); // Reset search when city changes
      loadProperties("", 1, false);
    }
  }, [selectedCityId, loadProperties]);

  // Debounced search effect
  useEffect(() => {
    if (selectedCityId == null) return;

    const timeoutId = setTimeout(() => {
      loadProperties(propertySearchTerm, 1, false);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [propertySearchTerm, selectedCityId, loadProperties]);

  // TEMPORARILY DISABLED - Ensure all properties in city selections have initialized room data
  // useEffect(() => {
  //   if (!useCreateFullAPI) return;

  //   let needsUpdate = false;
  //   const updates: { [key: number]: PropertyRoomData } = {};

  //   citySelections.forEach((citySelection) => {
  //     citySelection.properties.forEach((property) => {
  //       if (!propertyRoomData[property.id!]) {
  //         needsUpdate = true;
  //         updates[property.id!] = {
  //           propertyId: property.id!,
  //           property: property,
  //           selectedRooms: [],
  //           roomCharges: {},
  //           roomComplementaries: {},
  //           isRMCClient: true,
  //         };
  //       }
  //     });
  //   });

  //   // Only update state if there are actually new properties to initialize
  //   if (needsUpdate) {
  //     setPropertyRoomData((prev) => ({ ...prev, ...updates }));
  //   }
  // }, [citySelections, useCreateFullAPI]); // Keep dependencies but batch updates

  const onChangeField = (key: keyof ProposalFormState, value: any) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const validateStep1 = (): string | null => {
    if (!form.name.trim()) return "Name is required";
    if (!form.startDate) return "Start Date is required";
    if (!form.endDate) return "End Date is required";
    if (form.startDate && form.endDate && form.endDate < form.startDate)
      return "End Date must be after Start Date";

    // For edit mode, we should have rmcClientMappingId from the editProposal
    // For create mode, we should have it from the navigation state
    if (!rmcClientMappingId) {
      if (editMode) {
        console.warn(
          "[ProposalWizard] Missing rmcClientMappingId in edit mode. editProposal object:",
          editProposal
        );
        console.warn(
          "[ProposalWizard] Checked sources - state:",
          location.state?.rmcClientMappingId,
          "param:",
          rmcClientMappingIdParam,
          "editProposal.rmcClientMappingId:",
          editProposal?.rmcClientMappingId,
          "sessionStorage:",
          sessionStorage.getItem("rmcClientMappingId")
        );
        return "Cannot edit proposal: Missing RMC Client Mapping information. Please reopen from the proposals list.";
      } else {
        console.warn(
          "[ProposalWizard] Missing rmcClientMappingId in create mode. Navigation state:",
          state,
          "URL param:",
          rmcClientMappingIdParam
        );
        return "Missing RMC Client Mapping context. Return to client list and relaunch proposal creation.";
      }
    }

    return null;
  };

  const validateStep2 = (): string | null => {
    if (citySelections.length === 0 && selectedProperties.length === 0) {
      return "Please select at least one city with properties";
    }
    return null;
  };

  const validateStep3 = (): string | null => {
    // Validate that no property terms exceed the maximum character limit
    const propertiesExceedingLimit: string[] = [];

    for (const property of selectedProperties) {
      const propertyId = property.id!;
      const charCount = propertyTermsCharCount[propertyId] || 0;

      if (charCount > MAX_TERMS_LENGTH) {
        propertiesExceedingLimit.push(
          property.name || `Property ${propertyId}`
        );
      }
    }

    if (propertiesExceedingLimit.length > 0) {
      return `Terms & Conditions exceed ${MAX_TERMS_LENGTH} character limit for: ${propertiesExceedingLimit.join(
        ", "
      )}. Please reduce the content.`;
    }

    return null;
  };

  const validate = (): string | null => {
    const step1Error = validateStep1();
    if (step1Error) return step1Error;

    const step2Error = validateStep2();
    if (step2Error) return step2Error;

    const step3Error = validateStep3();
    if (step3Error) return step3Error;

    return null;
  };

  // New method to initialize property room data when a property is selected
  const initializePropertyRoomData = (property: Property) => {
    setPropertyRoomData((prev) => ({
      ...prev,
      [property.id!]: {
        propertyId: property.id!,
        property: property,
        selectedRooms: [],
        roomCharges: {},
        roomComplementaries: {},
        isRMCClient: true, // Default to true, can be changed in UI
      },
    }));
  };

  // Method to update room data for a property
  const updatePropertyRoomData = (
    propertyId: number,
    data: Partial<Omit<PropertyRoomData, "propertyId" | "property">>
  ) => {
    setPropertyRoomData((prev) => ({
      ...prev,
      [propertyId]: {
        ...prev[propertyId],
        ...data,
      },
    }));
  };

  // Method to create proposal using the comprehensive CreateFull API
  const createProposalFull = async (
    overrideStatus?: number
  ): Promise<boolean> => {
    try {
      console.log("=== DEBUG: Current propertyRoomData state ===");
      // Log without property objects to avoid circular references
      Object.entries(propertyRoomData).forEach(([pid, data]) => {
        console.log(`Property ${pid}:`, {
          propertyId: data.propertyId,
          selectedRooms: data.selectedRooms,
          roomChargesCount: Object.keys(data.roomCharges || {}).length,
          roomComplementariesCount: Object.keys(data.roomComplementaries || {})
            .length,
          isRMCClient: data.isRMCClient,
        });
      });

      console.log("=== DEBUG: Current roomDraftsRef ===");
      console.log(JSON.stringify(roomDraftsRef.current, null, 2));

      // Merge any transient room drafts into a working copy so we never lose recent edits
      const mergedPropertyRoomData: Record<
        number,
        | PropertyRoomData
        | Partial<Omit<PropertyRoomData, "property" | "propertyId">>
      > = {};

      // First, copy all data from propertyRoomData with deep cloning
      // NOTE: We don't deep clone the 'property' object as it contains circular references
      for (const [pidKey, data] of Object.entries(propertyRoomData)) {
        const pid = Number(pidKey);
        mergedPropertyRoomData[pid] = {
          propertyId: (data as any).propertyId,
          property: (data as any).property, // Keep property reference (not serialized to API)
          selectedRooms: Array.isArray((data as any).selectedRooms)
            ? [...(data as any).selectedRooms]
            : [],
          roomCharges: (data as any).roomCharges
            ? JSON.parse(JSON.stringify((data as any).roomCharges))
            : {},
          roomComplementaries: (data as any).roomComplementaries
            ? JSON.parse(JSON.stringify((data as any).roomComplementaries))
            : {},
          isRMCClient: (data as any).isRMCClient ?? true,
        } as any;
      }

      // Then overlay drafts (if any) with deep cloning
      for (const [pidKey, draft] of Object.entries(roomDraftsRef.current)) {
        const pid = Number(pidKey);
        if (!mergedPropertyRoomData[pid]) {
          mergedPropertyRoomData[pid] = {} as any;
        }

        // Deep clone draft data to prevent reference sharing
        mergedPropertyRoomData[pid] = {
          ...mergedPropertyRoomData[pid],
          selectedRooms: Array.isArray(draft.selectedRooms)
            ? [...draft.selectedRooms]
            : (mergedPropertyRoomData[pid] as any).selectedRooms || [],
          roomCharges: draft.roomCharges
            ? JSON.parse(JSON.stringify(draft.roomCharges))
            : (mergedPropertyRoomData[pid] as any).roomCharges || {},
          roomComplementaries: draft.roomComplementaries
            ? JSON.parse(JSON.stringify(draft.roomComplementaries))
            : (mergedPropertyRoomData[pid] as any).roomComplementaries || {},
          isRMCClient:
            draft.isRMCClient ??
            (mergedPropertyRoomData[pid] as any).isRMCClient ??
            true,
        } as any;
      }

      console.log("=== DEBUG: Merged property room data ===");
      // Log without the property objects to avoid circular reference errors
      Object.entries(mergedPropertyRoomData).forEach(([pid, data]) => {
        console.log(`Property ${pid}:`, {
          propertyId: (data as any).propertyId,
          selectedRooms: (data as any).selectedRooms,
          roomChargesCount: Object.keys((data as any).roomCharges || {}).length,
          roomComplementariesCount: Object.keys(
            (data as any).roomComplementaries || {}
          ).length,
          isRMCClient: (data as any).isRMCClient,
        });
      });

      // Prepare property mappings from collected data (dedupe properties)
      const propertyMappings: ProposalPropertyMappingCreateDto[] = [];

      // Gather selected properties from persisted city selections and pending selection
      const rawSelectedProperties = [
        ...citySelections.flatMap((cs) => cs.properties),
        ...(selectedProperties.length && selectedCityId
          ? selectedProperties
          : []),
      ];

      // Deduplicate by property id
      const propertyMap = new Map<number, Property>();
      rawSelectedProperties.forEach((p) => {
        if (p?.id != null) propertyMap.set(Number(p.id), p);
      });

      const allSelectedProperties = Array.from(propertyMap.values());

      console.log(
        "All selected properties (deduped):",
        allSelectedProperties.map((p) => ({ id: p.id, name: p.name }))
      );

      for (const property of allSelectedProperties) {
        console.log(
          `\n====== Processing Property ${property.id} (${property.name}) ======`
        );

        // Get merged data for this specific property
        const mergedData = mergedPropertyRoomData[property.id!];

        console.log(
          `[Property ${property.id}] Merged data exists:`,
          !!mergedData
        );
        if (mergedData) {
          console.log(
            `[Property ${property.id}] Merged selectedRooms:`,
            mergedData.selectedRooms
          );
        }

        // Create a FRESH deep clone for this property to ensure complete isolation
        // Note: We only clone the serializable data (selectedRooms, charges, complementaries)
        // We don't clone the 'property' object as it has circular references
        const roomData = mergedData
          ? {
              selectedRooms: Array.isArray(mergedData.selectedRooms)
                ? [...mergedData.selectedRooms] // Simple array copy is sufficient
                : [],
              roomCharges: mergedData.roomCharges
                ? JSON.parse(JSON.stringify(mergedData.roomCharges))
                : {},
              roomComplementaries: mergedData.roomComplementaries
                ? JSON.parse(JSON.stringify(mergedData.roomComplementaries))
                : {},
              isRMCClient: mergedData.isRMCClient ?? true,
            }
          : undefined;

        console.log(`[Property ${property.id}] Room data after cloning:`, {
          selectedRooms: roomData?.selectedRooms || [],
          selectedRoomsCount: roomData?.selectedRooms?.length || 0,
        });

        const mapping: ProposalPropertyMappingCreateDto = {
          propertyId: Number(property.id),
          isRMCClient: (roomData as any)?.isRMCClient ?? true,
          rmcClientRoomMappings: [],
          rmcRoomCharges: [],
          rmcRoomComplementaryMappings: [],
          rmcTerms: propertyTerms[Number(property.id)] || undefined, // Include RMC-overridden terms
        };

        console.log(`=== DEBUG: Property ${property.id} Mapping ===`);
        console.log("propertyTerms state:", propertyTerms);
        console.log(
          "Terms for this property:",
          propertyTerms[Number(property.id)]
        );
        console.log("Mapping object:", JSON.stringify(mapping, null, 2));

        if (roomData) {
          console.log(
            `[Property ${property.id}] Processing room data:`,
            JSON.stringify({
              selectedRooms: roomData.selectedRooms,
              roomChargesCount: Object.keys(roomData.roomCharges || {}).length,
              roomComplementariesCount: Object.keys(
                roomData.roomComplementaries || {}
              ).length,
            })
          );

          // Add room mappings (ensure numeric roomId)
          // Defensive copy and dedupe to prevent accidental cross-property leakage
          const selectedRoomsRaw = (roomData as any).selectedRooms || [];
          const selectedRooms: number[] = Array.isArray(selectedRoomsRaw)
            ? Array.from(new Set(selectedRoomsRaw.map((r: any) => Number(r))))
            : [];

          console.log(
            `[Property ${property.id}] Selected room IDs (deduped):`,
            selectedRooms
          );

          mapping.rmcClientRoomMappings = selectedRooms.map(
            (roomId) =>
              ({
                roomId: Number(roomId),
                isRMCClient: Boolean((roomData as any).isRMCClient),
              } as RMCClientRoomMappingCreateDto)
          );

          console.log(
            `[Property ${property.id}] Created ${mapping.rmcClientRoomMappings.length} room mappings:`,
            mapping.rmcClientRoomMappings.map((m) => m.roomId)
          );

          // Add room charges (only include positive amounts)
          const roomChargesObj = ((roomData as any).roomCharges ||
            {}) as Record<string, { amount: number; taxPercentage?: number }>;

          mapping.rmcRoomCharges = Object.entries(roomChargesObj)
            .filter(
              ([, charge]) =>
                !!charge &&
                typeof charge.amount === "number" &&
                charge.amount > 0
            )
            .map(((
              entry: [string, { amount: number; taxPercentage?: number }]
            ) => {
              const [roomId, charge] = entry;
              return {
                roomId: Number(roomId),
                amount: charge.amount,
                taxPercentage: charge.taxPercentage,
              } as RMCRoomChargeCreateDto;
            }) as any);

          // Add complementary mappings (flatten arrays)
          const roomComplementariesObj = ((roomData as any)
            .roomComplementaries || {}) as Record<string, number[]>;
          mapping.rmcRoomComplementaryMappings = Object.entries(
            roomComplementariesObj
          ).flatMap(([roomId, complementaryIds]) =>
            (Array.isArray(complementaryIds) ? complementaryIds : []).map(
              (complementaryId) => ({
                roomId: Number(roomId),
                complementaryId: Number(complementaryId),
              })
            )
          );
        } else {
          console.log(`No room data found for property ${property.id}`);
        }

        propertyMappings.push(mapping);
      }

      const proposalPayload: ProposalCreateFullPayload = {
        name: form.name.trim(),
        referenceCode: form.referenceCode?.trim(),
        description: form.description?.trim() || undefined,
        startDate: formatDate(form.startDate!),
        endDate: formatDate(form.endDate!),
        rmcClientMappingId: rmcClientMappingId!,
        propertyMappings: propertyMappings,
        currencyId: form.currencyId ?? undefined,
        isShowPrice: form.isShowPrice ?? true,
        creditPeriod: form.creditPeriod ?? 0,
        // Set status: if overrideStatus is provided use it, otherwise default to 1 (Proposed) for submit
        status: overrideStatus !== undefined ? overrideStatus : 1,
      };

      console.log("Creating proposal with payload:", proposalPayload);
      console.log(
        "Property mappings detail:",
        JSON.stringify(propertyMappings, null, 2)
      );

      // Final verification: Log room mappings per property to ensure no duplication
      console.log("=== Final Verification: Room Mappings Per Property ===");
      propertyMappings.forEach((mapping) => {
        const roomMappings = mapping.rmcClientRoomMappings || [];
        console.log(
          `Property ${mapping.propertyId}: ${roomMappings.length} rooms -`,
          roomMappings.map((rm) => rm.roomId)
        );
      });
      console.log("========================================================");

      const result = await proposalService.createProposalFull(proposalPayload);
      setProposalId(result.id);
      setCurrentProposalStatus(
        overrideStatus !== undefined ? overrideStatus : 1
      ); // Set local status

      // Terms are now handled by the backend via propertyMappings.rmcTerms
      // No need for separate API calls

      Swal.fire({
        icon: "success",
        title: "Comprehensive proposal created successfully!",
        text: `Proposal "${result.name}" with all room details has been created.`,
        timer: 3000,
        toast: true,
        position: "bottom",
        showConfirmButton: false,
      });

      return true;
    } catch (error: any) {
      console.error("Error creating comprehensive proposal:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to create proposal",
        text: error.message || "Failed to create comprehensive proposal",
      });
      return false;
    }
  };

  // Method to update an existing proposal using the UpdateFull API
  const updateProposalFull = async (
    overrideStatus?: number
  ): Promise<boolean> => {
    try {
      if (!proposalId) {
        throw new Error("Cannot update proposal: proposalId is missing");
      }

      console.log("=== DEBUG: Updating proposal ===", proposalId);
      console.log("=== DEBUG: Current propertyRoomData state ===");
      // Log without property objects to avoid circular references
      Object.entries(propertyRoomData).forEach(([pid, data]) => {
        console.log(`Property ${pid}:`, {
          propertyId: data.propertyId,
          selectedRooms: data.selectedRooms,
          roomChargesCount: Object.keys(data.roomCharges || {}).length,
          roomComplementariesCount: Object.keys(data.roomComplementaries || {})
            .length,
          isRMCClient: data.isRMCClient,
        });
      });

      console.log("=== DEBUG: Current roomDraftsRef ===");
      console.log(JSON.stringify(roomDraftsRef.current, null, 2));

      // Merge any transient room drafts into a working copy
      const mergedPropertyRoomData: Record<
        number,
        | PropertyRoomData
        | Partial<Omit<PropertyRoomData, "property" | "propertyId">>
      > = {};

      // First, copy all data from propertyRoomData with deep cloning
      for (const [pidKey, data] of Object.entries(propertyRoomData)) {
        const pid = Number(pidKey);
        mergedPropertyRoomData[pid] = {
          propertyId: (data as any).propertyId,
          property: (data as any).property,
          selectedRooms: Array.isArray((data as any).selectedRooms)
            ? [...(data as any).selectedRooms]
            : [],
          roomCharges: (data as any).roomCharges
            ? JSON.parse(JSON.stringify((data as any).roomCharges))
            : {},
          roomComplementaries: (data as any).roomComplementaries
            ? JSON.parse(JSON.stringify((data as any).roomComplementaries))
            : {},
          isRMCClient: (data as any).isRMCClient ?? true,
        } as any;
      }

      // Then overlay drafts (if any) with deep cloning
      for (const [pidKey, draft] of Object.entries(roomDraftsRef.current)) {
        const pid = Number(pidKey);
        if (!mergedPropertyRoomData[pid]) {
          mergedPropertyRoomData[pid] = {} as any;
        }

        mergedPropertyRoomData[pid] = {
          ...mergedPropertyRoomData[pid],
          selectedRooms: Array.isArray(draft.selectedRooms)
            ? [...draft.selectedRooms]
            : (mergedPropertyRoomData[pid] as any).selectedRooms || [],
          roomCharges: draft.roomCharges
            ? JSON.parse(JSON.stringify(draft.roomCharges))
            : (mergedPropertyRoomData[pid] as any).roomCharges || {},
          roomComplementaries: draft.roomComplementaries
            ? JSON.parse(JSON.stringify(draft.roomComplementaries))
            : (mergedPropertyRoomData[pid] as any).roomComplementaries || {},
          isRMCClient:
            draft.isRMCClient ??
            (mergedPropertyRoomData[pid] as any).isRMCClient ??
            true,
        } as any;
      }

      console.log("=== DEBUG: Merged property room data ===");
      Object.entries(mergedPropertyRoomData).forEach(([pid, data]) => {
        console.log(`Property ${pid}:`, {
          propertyId: (data as any).propertyId,
          selectedRooms: (data as any).selectedRooms,
          roomChargesCount: Object.keys((data as any).roomCharges || {}).length,
          roomComplementariesCount: Object.keys(
            (data as any).roomComplementaries || {}
          ).length,
          isRMCClient: (data as any).isRMCClient,
        });
      });

      // Prepare property mappings from collected data
      const propertyMappings: ProposalPropertyMappingCreateDto[] = [];

      // Gather selected properties from persisted city selections and pending selection
      const rawSelectedProperties = [
        ...citySelections.flatMap((cs) => cs.properties),
        ...(selectedProperties.length && selectedCityId
          ? selectedProperties
          : []),
      ];

      // Deduplicate by property id
      const propertyMap = new Map<number, Property>();
      rawSelectedProperties.forEach((p) => {
        if (p?.id != null) propertyMap.set(Number(p.id), p);
      });

      const allSelectedProperties = Array.from(propertyMap.values());

      console.log(
        "All selected properties (deduped):",
        allSelectedProperties.map((p) => ({ id: p.id, name: p.name }))
      );

      for (const property of allSelectedProperties) {
        console.log(
          `\n====== Processing Property ${property.id} (${property.name}) ======`
        );

        const mergedData = mergedPropertyRoomData[property.id!];

        console.log(
          `[Property ${property.id}] Merged data exists:`,
          !!mergedData
        );
        if (mergedData) {
          console.log(
            `[Property ${property.id}] Merged selectedRooms:`,
            mergedData.selectedRooms
          );
        }

        const roomData = mergedData
          ? {
              selectedRooms: Array.isArray(mergedData.selectedRooms)
                ? [...mergedData.selectedRooms]
                : [],
              roomCharges: mergedData.roomCharges
                ? JSON.parse(JSON.stringify(mergedData.roomCharges))
                : {},
              roomComplementaries: mergedData.roomComplementaries
                ? JSON.parse(JSON.stringify(mergedData.roomComplementaries))
                : {},
              isRMCClient: mergedData.isRMCClient ?? true,
            }
          : undefined;

        console.log(`[Property ${property.id}] Room data after cloning:`, {
          selectedRooms: roomData?.selectedRooms || [],
          selectedRoomsCount: roomData?.selectedRooms?.length || 0,
        });

        const mapping: ProposalPropertyMappingCreateDto = {
          propertyId: Number(property.id),
          isRMCClient: (roomData as any)?.isRMCClient ?? true,
          rmcClientRoomMappings: [],
          rmcRoomCharges: [],
          rmcRoomComplementaryMappings: [],
          rmcTerms: propertyTerms[Number(property.id)] || undefined, // Include RMC-overridden terms for UPDATE
        };

        console.log(`=== DEBUG UPDATE: Property ${property.id} Mapping ===`);
        console.log("propertyTerms state:", propertyTerms);
        console.log(
          "Terms for this property:",
          propertyTerms[Number(property.id)]
        );
        console.log("Mapping object:", JSON.stringify(mapping, null, 2));

        if (roomData) {
          console.log(
            `[Property ${property.id}] Processing room data:`,
            JSON.stringify({
              selectedRooms: roomData.selectedRooms,
              roomChargesCount: Object.keys(roomData.roomCharges || {}).length,
              roomComplementariesCount: Object.keys(
                roomData.roomComplementaries || {}
              ).length,
            })
          );

          const selectedRoomsRaw = (roomData as any).selectedRooms || [];
          const selectedRooms: number[] = Array.isArray(selectedRoomsRaw)
            ? Array.from(new Set(selectedRoomsRaw.map((r: any) => Number(r))))
            : [];

          console.log(
            `[Property ${property.id}] Selected room IDs (deduped):`,
            selectedRooms
          );

          mapping.rmcClientRoomMappings = selectedRooms.map(
            (roomId) =>
              ({
                roomId: Number(roomId),
                isRMCClient: Boolean((roomData as any).isRMCClient),
              } as RMCClientRoomMappingCreateDto)
          );

          console.log(
            `[Property ${property.id}] Created ${mapping.rmcClientRoomMappings.length} room mappings:`,
            mapping.rmcClientRoomMappings.map((m) => m.roomId)
          );

          const roomChargesObj = ((roomData as any).roomCharges ||
            {}) as Record<string, { amount: number; taxPercentage?: number }>;

          mapping.rmcRoomCharges = Object.entries(roomChargesObj)
            .filter(
              ([, charge]) =>
                !!charge &&
                typeof charge.amount === "number" &&
                charge.amount > 0
            )
            .map(((
              entry: [string, { amount: number; taxPercentage?: number }]
            ) => {
              const [roomId, charge] = entry;
              return {
                roomId: Number(roomId),
                amount: charge.amount,
                taxPercentage: charge.taxPercentage,
              } as RMCRoomChargeCreateDto;
            }) as any);

          const roomComplementariesObj = ((roomData as any)
            .roomComplementaries || {}) as Record<string, number[]>;
          mapping.rmcRoomComplementaryMappings = Object.entries(
            roomComplementariesObj
          ).flatMap(([roomId, complementaryIds]) =>
            (Array.isArray(complementaryIds) ? complementaryIds : []).map(
              (complementaryId) => ({
                roomId: Number(roomId),
                complementaryId: Number(complementaryId),
              })
            )
          );
        } else {
          console.log(`No room data found for property ${property.id}`);
        }

        propertyMappings.push(mapping);
      }

      const proposalPayload: any = {
        id: proposalId, // Required by ProposalUpdateFullDto
        name: form.name.trim(),
        referenceCode: form.referenceCode?.trim(),
        description: form.description?.trim() || undefined,
        startDate: formatDate(form.startDate!),
        endDate: formatDate(form.endDate!),
        rmcClientMappingId: rmcClientMappingId!,
        propertyMappings: propertyMappings,
        currencyId: form.currencyId ?? undefined,
        isShowPrice: form.isShowPrice ?? true,
        creditPeriod: form.creditPeriod ?? 0,
        status: overrideStatus !== undefined ? overrideStatus.toString() : "1", // Backend expects string
      };

      console.log("Updating proposal with payload:", proposalPayload);
      console.log(
        "Property mappings detail:",
        JSON.stringify(propertyMappings, null, 2)
      );

      console.log("=== Final Verification: Room Mappings Per Property ===");
      propertyMappings.forEach((mapping) => {
        const roomMappings = mapping.rmcClientRoomMappings || [];
        console.log(
          `Property ${mapping.propertyId}: ${roomMappings.length} rooms -`,
          roomMappings.map((rm) => rm.roomId)
        );
      });
      console.log("========================================================");

      const result = await proposalService.updateProposalFull(
        proposalId,
        proposalPayload
      );
      setCurrentProposalStatus(
        overrideStatus !== undefined ? overrideStatus : currentProposalStatus
      ); // Update local status

      console.log("=== DEBUG: Update Result ===");
      console.log("result:", JSON.stringify(result, null, 2));

      // Terms are now handled by the backend via propertyMappings.rmcTerms
      // No need for separate terms handling in update

      Swal.fire({
        icon: "success",
        title: "Proposal updated successfully!",
        text: `Proposal "${result.name}" with all room details has been updated.`,
        timer: 3000,
        toast: true,
        position: "bottom",
        showConfirmButton: false,
      });

      return true;
    } catch (error: any) {
      console.error("Error updating proposal:", error);
      Swal.fire({
        icon: "error",
        title: "Failed to update proposal",
        text: error.message || "Failed to update proposal",
      });
      return false;
    }
  };

  const createProposalAndMappings = async (): Promise<boolean> => {
    try {
      const persisted = citySelections.map((sel) => ({
        cityId: sel.cityId,
        propertyIds: sel.properties.map((p) => Number(p.id)),
      }));
      const hasPending =
        selectedProperties.length &&
        selectedCityId &&
        !citySelections.some((cs) => cs.cityId === selectedCityId);
      const pending = hasPending
        ? [
            {
              cityId: Number(selectedCityId),
              propertyIds: selectedProperties.map((p) => Number(p.id)),
            },
          ]
        : [];
      const cityProperties = [...persisted, ...pending];

      const proposalPayload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        startDate: form.startDate ? formatDate(form.startDate) : undefined,
        endDate: form.endDate ? formatDate(form.endDate) : undefined,
        rmcClientMappingId: rmcClientMappingId,
        cityProperties,
      };

      const proposalResult = await httpService.post<{ id: number }>(
        "/api/Proposal/Create",
        proposalPayload,
        undefined,
        { unwrap: true, normalize: false, toast: false }
      );
      const newProposalId = proposalResult.id;
      setProposalId(newProposalId);

      const mappings: PropertyMappingResult["mappedProperties"] = [];

      for (const cityProperty of cityProperties) {
        for (const propertyId of cityProperty.propertyIds) {
          try {
            const mappingResult = await httpService.post<{
              clientPropertyMappingId: number;
            }>(
              `/api/Property/CreateClientPropertyMappings/${newProposalId}/${cityProperty.cityId}`,
              [propertyId],
              undefined,
              { unwrap: true, normalize: false, toast: false }
            );

            mappings.push({
              cityId: cityProperty.cityId,
              propertyId: propertyId,
              clientPropertyMappingId: mappingResult.clientPropertyMappingId,
            });
          } catch (error) {
            console.error(
              `Failed to create mapping for property ${propertyId} in city ${cityProperty.cityId}:`,
              error
            );
          }
        }
      }

      setPropertyMappings(mappings);

      Swal.fire({
        icon: "success",
        title: "Proposal and property mappings created successfully",
        timer: 2000,
        toast: true,
        position: "bottom",
        showConfirmButton: false,
      });

      return true;
    } catch (error: any) {
      console.error("Error creating proposal and mappings:", error);
      Swal.fire({
        icon: "error",
        title:
          error.message || "Failed to create proposal and property mappings",
      });
      return false;
    }
  };

  const nextStep = async () => {
    let currentStepError = null;

    if (wizardState.activeIndex === 0) {
      currentStepError = validateStep1();
    } else if (wizardState.activeIndex === 1) {
      // For step 2 (Property Selection), validate both property selection and room management
      currentStepError = validateStep2();
    } else if (wizardState.activeIndex === 2) {
      // For step 3 (Terms & Conditions), validate character limits
      currentStepError = validateStep3();
    } else if (wizardState.activeIndex === 3) {
      // For step 4 (Preview & Submit), submit the proposal
      if (!currentStepError) {
        await submitProposal();
        return;
      }
    }

    if (currentStepError) {
      Swal.fire({ icon: "warning", title: currentStepError });
      return;
    }

    // Save proposal before moving to next step
    // Skip auto-save for Approved/Expired (read-only) proposals
    if (!viewMode && !isReadOnlyProposal) {
      // Check if there are any changes before making API calls
      const hasChanges = hasProposalChanges();

      if (!hasChanges && proposalId) {
        console.log("=== No changes detected, skipping auto-save ===");
      } else {
        try {
          let saved = false;

          if (isDraftProposal) {
            // Draft: Update with status = 0 (Draft)
            if (proposalId) {
              saved = await updateProposalFull(0);
            } else {
              saved = await createProposalFull(0);
            }
          } else if (isProposedProposal) {
            // Proposed: Update with status = 1 (Proposed)
            if (proposalId) {
              saved = await updateProposalFull(1);
            } else {
              saved = await createProposalFull(1);
            }
          } else if (!proposalId) {
            // New proposal (no status yet): Create as Draft
            saved = await createProposalFull(0);
          }

          if (!saved) {
            Swal.fire({
              icon: "error",
              title: "Failed to save proposal",
            });
            return;
          }

          // Update the snapshot after successful save
          initialProposalSnapshot.current = createProposalSnapshot();
          console.log("=== Snapshot updated after save ===");

          // Show a subtle success notification
          Swal.fire({
            icon: "success",
            title: "Progress saved",
            timer: 1500,
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
          });
        } catch (err: any) {
          console.error("Failed to save on next step:", err);
          Swal.fire({
            icon: "error",
            title: err?.message || "Failed to save progress",
          });
          return;
        }
      }
    }

    const newCompletedSteps = [...wizardState.completedSteps];
    newCompletedSteps[wizardState.activeIndex] = true;

    setWizardState((prev) => ({
      activeIndex: Math.min(prev.activeIndex + 1, wizardSteps.length - 1),
      completedSteps: newCompletedSteps,
    }));
  };

  const prevStep = async () => {
    // Auto-save when going back from any step (except Step 1 which is the first step)
    // Step 4 (Preview) → Step 3 (Terms)
    // Step 3 (Terms) → Step 2 (Room Selection)
    // Step 2 (Room Selection) → Step 1 (Basic Info)
    // Skip auto-save for Approved/Expired (read-only) proposals
    if (!viewMode && !isReadOnlyProposal && wizardState.activeIndex > 0) {
      const hasChanges = hasProposalChanges();

      if (hasChanges || !proposalId) {
        console.log(
          `=== Auto-save on prevStep from Step ${
            wizardState.activeIndex + 1
          } to Step ${wizardState.activeIndex} ===`
        );

        try {
          let saved = false;

          if (isDraftProposal) {
            // Draft: Update with status = 0 (Draft)
            if (proposalId) {
              saved = await updateProposalFull(0);
            } else {
              saved = await createProposalFull(0);
            }
          } else if (isProposedProposal) {
            // Proposed: Update with status = 1 (Proposed)
            if (proposalId) {
              saved = await updateProposalFull(1);
            } else {
              saved = await createProposalFull(1);
            }
          } else if (!proposalId) {
            // New proposal (no status yet): Create as Draft
            saved = await createProposalFull(0);
          }

          if (saved) {
            // Update the snapshot after successful save
            initialProposalSnapshot.current = createProposalSnapshot();
            console.log("=== Auto-save on prevStep: Snapshot updated ===");

            // Show subtle success notification
            Swal.fire({
              icon: "success",
              title: "Progress saved",
              timer: 1500,
              toast: true,
              position: "bottom-end",
              showConfirmButton: false,
            });
          }
        } catch (err: any) {
          console.error("Failed to save on prev step:", err);
          // Don't block navigation on save failure, but show warning
          Swal.fire({
            icon: "warning",
            title: "Failed to save progress",
            text: err?.message || "Your changes may not be saved",
            timer: 2000,
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
          });
        }
      } else {
        console.log(
          `=== No changes detected on prevStep from Step ${
            wizardState.activeIndex + 1
          }, skipping auto-save ===`
        );
      }
    }

    setWizardState((prev) => ({
      ...prev,
      activeIndex: Math.max(prev.activeIndex - 1, 0),
    }));
  };

  const goToStep = (index: number) => {
    const canGoToStep =
      index === 0 ||
      wizardState.completedSteps
        .slice(0, index)
        .every((completed) => completed);
    if (canGoToStep) {
      setWizardState((prev) => ({
        ...prev,
        activeIndex: index,
      }));
    }
  };

  const submitProposal = async () => {
    const error = validate();
    if (error) {
      Swal.fire({ icon: "warning", title: error });
      return;
    }

    // Check for changes before submitting
    const hasChanges = hasProposalChanges();
    if (!hasChanges && proposalId && !isDraftProposal) {
      console.log("=== No changes detected, skipping update ===");
      Swal.fire({
        icon: "info",
        title: "No changes to save",
        text: "The proposal has not been modified.",
        timer: 2000,
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
      });
      // Still navigate back
      navigate(`/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`, {
        state: { rmcId, clientId, clientName, rmcClientMappingId },
      });
      return;
    }

    try {
      let result = false;

      // Handle different scenarios based on proposal state
      if (!proposalId) {
        // Scenario 1: Creating a brand new proposal (no draft saved yet)
        if (useCreateFullAPI) {
          result = await createProposalFull(1); // Status = 1 (Proposed)
        } else {
          result = await createProposalAndMappings();
        }
      } else if (isDraftProposal) {
        // Scenario 2: Submitting an existing draft proposal
        // Must UPDATE the draft (not create duplicate) and change status to Proposed
        if (useCreateFullAPI) {
          result = await updateProposalFull(1); // Status = 1 (Proposed)
        } else {
          Swal.fire({
            icon: "error",
            title: "Update not implemented for legacy API mode",
          });
          return;
        }
      } else {
        // Scenario 3: Editing an existing non-draft proposal
        if (useCreateFullAPI) {
          result = await updateProposalFull(1);
        } else {
          Swal.fire({
            icon: "error",
            title: "Update not implemented for legacy API mode",
          });
          return;
        }
      }

      if (!result) return;

      Swal.fire({
        icon: "success",
        title: `Proposal ${editMode ? "updated" : "completed"} successfully!`,
        timer: 2200,
        toast: true,
        position: "bottom",
        showConfirmButton: false,
      });

      // Navigate back to the proposals list
      navigate(`/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`, {
        state: { rmcId, clientId, clientName, rmcClientMappingId },
      });
    } catch (e: any) {
      Swal.fire({
        icon: "error",
        title: e.message || "Failed to complete proposal workflow.",
      });
    }
  };

  // Property management functions
  const togglePropertySelect = (property: Property) => {
    const exists = selectedProperties.some((p) => p.id === property.id);

    if (exists) {
      // Remove property and its room data
      setPropertyRoomData((prevData) => {
        const { [property.id!]: removed, ...rest } = prevData;
        return rest;
      });

      // Update both selectedProperties and citySelections
      setSelectedProperties((prev) => {
        const updated = prev.filter((p) => p.id !== property.id);

        // Auto-update citySelections to reflect the removal
        if (selectedCityId) {
          setCitySelections((cityPrev) => {
            const existingSelection = cityPrev.find(
              (cs) => cs.cityId === selectedCityId
            );

            if (existingSelection) {
              // Update existing city selection with new properties list
              return cityPrev.map((cs) =>
                cs.cityId === selectedCityId
                  ? { ...cs, properties: updated }
                  : cs
              );
            }

            return cityPrev;
          });
        }

        return updated;
      });
    } else {
      // Add property and initialize room data
      initializePropertyRoomData(property);

      setSelectedProperties((prev) => {
        const updated = [...prev, property];

        // Auto-update citySelections when property is added
        if (selectedCityId) {
          const cityName =
            cities.find((c) => c.id === selectedCityId)?.name ||
            `City ${selectedCityId}`;

          setCitySelections((cityPrev) => {
            const existingSelection = cityPrev.find(
              (cs) => cs.cityId === selectedCityId
            );

            if (existingSelection) {
              // Update existing city selection with new properties
              return cityPrev.map((cs) =>
                cs.cityId === selectedCityId
                  ? { ...cs, properties: updated }
                  : cs
              );
            } else {
              // Create new city selection
              return [
                ...cityPrev,
                {
                  cityId: Number(selectedCityId),
                  cityName,
                  properties: updated,
                },
              ];
            }
          });
        }

        return updated;
      });

      // Automatically load rooms for the newly selected property
      console.log(
        `[togglePropertySelect] Auto-loading rooms for selected property: ${property.name}`
      );
      // Use setTimeout to ensure state updates complete before opening room management
      setTimeout(() => {
        handleManageRooms(property);
      }, 100);
    }
  };

  const isPropertySelected = (propertyId?: number) =>
    !!selectedProperties.find((p) => p.id === propertyId);

  // Sort displayedProperties to show selected ones at the top
  const sortedDisplayedProperties = useMemo(() => {
    return [...displayedProperties].sort((a, b) => {
      const aSelected = isPropertySelected(a.id);
      const bSelected = isPropertySelected(b.id);

      // Selected properties come first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Keep original order for properties with same selection status
      return 0;
    });
  }, [displayedProperties, selectedProperties]);

  // Auto-set city, load properties, and show first property's rooms when citySelections are loaded
  useEffect(() => {
    if (citySelections.length > 0 && !selectedCityId) {
      const firstCitySelection = citySelections[0];
      console.log(
        "[Auto-load City] Setting first city from selections:",
        firstCitySelection.cityName
      );

      // Set the city
      setSelectedCityId(firstCitySelection.cityId);

      // Set the city autocomplete value
      const cityOption = cities.find((c) => c.id === firstCitySelection.cityId);
      if (cityOption) {
        setCityACValue(cityOption);
      }

      // Properties are already in citySelections, so set selectedProperties
      if (firstCitySelection.properties.length > 0) {
        console.log(
          "[Auto-load City] Setting selected properties:",
          firstCitySelection.properties.length
        );
        setSelectedProperties(firstCitySelection.properties);

        // The loadProperties useEffect will trigger and load properties for the city
        // Then the auto-load rooms effect will trigger for the first property
      }
    }
  }, [citySelections, selectedCityId, cities]);

  const removeCitySelection = (cityId: number) => {
    setCitySelections((prev) => prev.filter((c) => c.cityId !== cityId));
  };

  const handleLoadMoreProperties = () => {
    if (!loadingProperties && hasMoreProperties) {
      loadProperties(propertySearchTerm, propertyPage + 1, true);
    }
  };

  const handlePreviewProperty = (property: Property) => {
    setPreviewProperty(property);
    setShowPropertyPreview(true);
  };

  const handleShowRooms = (property: Property) => {
    console.log("handleShowRooms called with property:", property.id);
    setSelectedPropertyForRoomView(property);
    setShowRoomList(true);
    // Close room management if it's open
    setShowRoomManagement(false);
    setSelectedPropertyForRooms(null);
  };

  const handleManageRooms = (property: Property) => {
    console.log(
      `[handleManageRooms] Opening room management for property ${property.id}`
    );

    // Log current state for this property
    const currentData = propertyRoomData[property.id!];
    const currentDraft = roomDraftsRef.current[property.id!];

    console.log(
      `[handleManageRooms] Current propertyRoomData for ${property.id}:`,
      {
        exists: !!currentData,
        selectedRooms: currentData?.selectedRooms || [],
      }
    );
    console.log(`[handleManageRooms] Current draft for ${property.id}:`, {
      exists: !!currentDraft,
      selectedRooms: currentDraft?.selectedRooms || [],
    });

    // ensure we have a slot for this property's room data before opening
    if (!propertyRoomData[property.id!]) {
      console.log(
        `[handleManageRooms] Initializing new room data for property ${property.id}`
      );
      initializePropertyRoomData(property);
    }

    setSelectedPropertyForRooms(property);
    setShowRoomManagement(true);
    // Close room list if it's open
    setShowRoomList(false);
    setSelectedPropertyForRoomView(null);
  };

  const handleCloseRoomManagement = () => {
    console.log(`[handleCloseRoomManagement] Closing room management`);
    setShowRoomManagement(false);
    setSelectedPropertyForRooms(null);

    // Force a small delay to ensure state is clean before opening another property
    // This prevents any potential state contamination
  };

  const handleCloseRoomList = () => {
    setShowRoomList(false);
    setSelectedPropertyForRoomView(null);
  };

  // Define backToUrl at the top-level of the component so it is always available
  const backToUrl =
    rmcId && rmcClientMappingId
      ? `/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`
      : "/clients";

  // Callback to receive room data from ProposalRoomView when using comprehensive API
  const handleRoomDataUpdate = useCallback(
    (
      propertyId: number,
      selectedRooms: number[],
      roomCharges: Record<number, { amount: number; taxPercentage?: number }>,
      roomComplementaries: Record<number, number[]>
    ) => {
      console.log(
        `[handleRoomDataUpdate] Property ${propertyId} - Received ${selectedRooms.length} rooms:`,
        selectedRooms
      );

      // Deep clone using JSON to ensure complete independence
      // This is safe because the incoming data should not have circular references
      const deepCloneSafe = (obj: any): any => {
        if (obj === null || obj === undefined) return obj;
        try {
          return JSON.parse(JSON.stringify(obj));
        } catch (e) {
          console.error(
            `Failed to clone object for property ${propertyId}:`,
            e
          );
          return obj;
        }
      };

      // Create COMPLETELY NEW objects with FULL deep clones
      // Important: Use JSON serialization to ensure no reference sharing whatsoever
      const newDraftData = {
        selectedRooms: deepCloneSafe(selectedRooms) || [],
        roomCharges: deepCloneSafe(roomCharges) || {},
        roomComplementaries: deepCloneSafe(roomComplementaries) || {},
      };

      console.log(
        `[handleRoomDataUpdate] Property ${propertyId} - Stored ${newDraftData.selectedRooms.length} rooms in draft:`,
        newDraftData.selectedRooms
      );

      // Store in drafts ref with completely new object
      // CRITICAL: Completely replace the draft for this property (no merging, no spreading old data)
      const freshDrafts: Record<number, any> = {};

      // Copy other properties' drafts (if any)
      for (const key in roomDraftsRef.current) {
        const pid = Number(key);
        if (pid !== propertyId) {
          freshDrafts[pid] = deepCloneSafe(roomDraftsRef.current[pid]);
        }
      }

      // Set the current property's draft (completely fresh, no old data)
      freshDrafts[propertyId] = newDraftData;

      // Replace the entire ref (not spread)
      roomDraftsRef.current = freshDrafts;

      console.log(
        `[handleRoomDataUpdate] All drafts after update:`,
        Object.keys(roomDraftsRef.current).map((key) => ({
          propertyId: key,
          roomCount:
            roomDraftsRef.current[Number(key)].selectedRooms?.length || 0,
          rooms: roomDraftsRef.current[Number(key)].selectedRooms,
        }))
      );

      // Update main state with completely new object (no spreading prev[propertyId])
      setPropertyRoomData((prev) => {
        const prevPropertyData = prev[propertyId];

        return {
          ...prev,
          [propertyId]: {
            propertyId: prevPropertyData?.propertyId ?? propertyId,
            property: prevPropertyData?.property ?? ({} as Property),
            isRMCClient: prevPropertyData?.isRMCClient ?? true,
            // Use FRESH deep clones from newDraftData (not shared references)
            selectedRooms: deepCloneSafe(newDraftData.selectedRooms),
            roomCharges: deepCloneSafe(newDraftData.roomCharges),
            roomComplementaries: deepCloneSafe(
              newDraftData.roomComplementaries
            ),
          },
        };
      });

      console.log(
        `[handleRoomDataUpdate] Property ${propertyId} - Update complete`
      );
    },
    []
  ); // Empty dependency array since we're using refs and functional updates

  // Handler for when a room is selected in ProposalRoomView
  // This automatically marks the containing property as selected
  const handlePropertyAutoSelect = useCallback(
    (propertyId: number) => {
      console.log(
        `[handlePropertyAutoSelect] Auto-selecting property ${propertyId} because a room was selected`
      );

      // Get the property from available properties
      const allAvailableProperties = [
        ...displayedProperties,
        ...citySelections.flatMap((cs) => cs.properties),
      ];

      const property = allAvailableProperties.find((p) => p.id === propertyId);
      if (!property) {
        console.warn(
          `[handlePropertyAutoSelect] Could not find property ${propertyId}`
        );
        return;
      }

      // Check if property is already selected
      const isAlreadySelected = selectedProperties.some(
        (p) => p.id === propertyId
      );
      if (isAlreadySelected) {
        console.log(
          `[handlePropertyAutoSelect] Property ${propertyId} is already selected, no action needed`
        );
        return;
      }

      // Auto-select the property using existing logic
      console.log(
        `[handlePropertyAutoSelect] Toggling property ${propertyId} to selected`
      );
      togglePropertySelect(property);
    },
    [displayedProperties, citySelections, selectedProperties]
  );

  // Handler for when all rooms are deselected in ProposalRoomView
  // This automatically deselects the containing property
  const handlePropertyAutoDeselect = useCallback(
    (propertyId: number) => {
      console.log(
        `[handlePropertyAutoDeselect] Auto-deselecting property ${propertyId} because last room was deselected`
      );

      // Check if property is selected
      const isSelected = selectedProperties.some((p) => p.id === propertyId);
      if (!isSelected) {
        console.log(
          `[handlePropertyAutoDeselect] Property ${propertyId} is not selected, no action needed`
        );
        return;
      }

      // Find the property object
      const allAvailableProperties = [
        ...displayedProperties,
        ...citySelections.flatMap((cs) => cs.properties),
      ];

      const property = allAvailableProperties.find((p) => p.id === propertyId);
      if (!property) {
        console.warn(
          `[handlePropertyAutoDeselect] Could not find property ${propertyId}`
        );
        return;
      }

      // Auto-deselect the property using existing toggle logic
      console.log(
        `[handlePropertyAutoDeselect] Toggling property ${propertyId} to deselected`
      );
      togglePropertySelect(property);
    },
    [displayedProperties, citySelections, selectedProperties]
  );

  return (
    <>
      <div className="content">
        <PageHeader
          showBackButton
          onBack={() => {
            if (rmcClientMappingId) {
              navigate(`/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`, {
                state: { rmcId, clientId, clientName, rmcClientMappingId },
              });
            } else {
              navigate("/clients");
            }
          }}
          title={
            clientName
              ? `${
                  viewMode ? "View" : editMode ? "Edit" : "Create"
                } Proposal - ${clientName}`
              : `${viewMode ? "View" : editMode ? "Edit" : "Create"} Proposal`
          }
        />

        <div className="content-body">
          <div className="container mt-3">
            <div className="row shadow">
              <div className="col-12">
                <div className="card border-0">
                  <div className="card-body p-0">
                    {/* Wizard Steps */}
                    <div className="mb-0">
                      <Steps
                        model={wizardSteps}
                        activeIndex={wizardState.activeIndex}
                        onSelect={(e) => goToStep(e.index)}
                        readOnly={false}
                        className="steps-compact"
                      />
                    </div>

                    {/* Step Content */}
                    <div className="wizard-content px-3 pt-3 pb-2">
                      {/* Step 1: Create Proposal */}
                      {wizardState.activeIndex === 0 && (
                        <div className="wizard-step">
                          <div className="row g-3">
                            {/* Left Column: Details (70%) */}
                            <div className="col-lg-8">
                              {/* Section 1: Details */}
                              <div className="proposal-details-card">
                                <div className="card-header-modern">
                                  <i className="pi pi-file-edit me-2"></i>
                                  <span>Proposal Details</span>
                                </div>
                                <div className="card-body-modern">
                                  {/* Name Field - Full Width */}
                                  <div className="form-field-group mb-3">
                                    <label className="form-label-modern">
                                      <i className="pi pi-tag me-2"></i>
                                      Proposal Name *
                                    </label>
                                    {viewMode ? (
                                      <div className="form-control-plaintext fw-semibold">
                                        {form.name || "-"}
                                      </div>
                                    ) : (
                                      <InputText
                                        value={form.name}
                                        onChange={(e) =>
                                          onChangeField("name", e.target.value)
                                        }
                                        className="w-100 input-modern"
                                        placeholder="Enter a descriptive name for this proposal"
                                      />
                                    )}
                                  </div>

                                  {/* Date Range Row */}
                                  <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                      <div className="form-field-group">
                                        <label className="form-label-modern">
                                          <i className="pi pi-calendar me-2"></i>
                                          Start Date *
                                        </label>
                                        {viewMode ? (
                                          <div className="form-control-plaintext fw-semibold">
                                            {form.startDate
                                              ? formatDate(form.startDate)
                                              : "-"}
                                          </div>
                                        ) : (
                                          <Calendar
                                            value={form.startDate}
                                            onChange={(e) =>
                                              onChangeField(
                                                "startDate",
                                                e.value
                                              )
                                            }
                                            showIcon
                                            className="w-100 calendar-modern"
                                            dateFormat="yy-mm-dd"
                                            placeholder="Select start date"
                                            showButtonBar
                                            minDate={new Date()}
                                          />
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="form-field-group">
                                        <label className="form-label-modern">
                                          <i className="pi pi-calendar-plus me-2"></i>
                                          End Date *
                                        </label>
                                        {viewMode ? (
                                          <div className="form-control-plaintext fw-semibold">
                                            {form.endDate
                                              ? formatDate(form.endDate)
                                              : "-"}
                                          </div>
                                        ) : (
                                          <Calendar
                                            value={form.endDate}
                                            onChange={(e) =>
                                              onChangeField("endDate", e.value)
                                            }
                                            showIcon
                                            className="w-100 calendar-modern"
                                            dateFormat="yy-mm-dd"
                                            placeholder="Select end date"
                                            showButtonBar
                                            minDate={
                                              form.startDate || new Date()
                                            }
                                          />
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Description Field */}
                                  <div className="form-field-group mb-0">
                                    <label className="form-label-modern">
                                      <i className="pi pi-align-left me-2"></i>
                                      Description
                                    </label>
                                    {viewMode ? (
                                      <div className="form-control-plaintext">
                                        {form.description || "-"}
                                      </div>
                                    ) : (
                                      <InputTextarea
                                        autoResize
                                        value={form.description}
                                        onChange={(e) =>
                                          onChangeField(
                                            "description",
                                            e.target.value
                                          )
                                        }
                                        className="w-100 textarea-modern"
                                        rows={3}
                                        placeholder="Add optional notes or description for this proposal"
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Right Column: Configuration (30%) */}
                            <div className="col-lg-4">
                              {/* Section 2: Configuration */}
                              <div className="proposal-config-card">
                                <div className="card-header-modern">
                                  <i className="pi pi-cog me-2"></i>
                                  <span>Configuration</span>
                                </div>
                                <div className="card-body-modern">
                                  {/* Credit Period */}
                                  <div className="form-field-group mb-3">
                                    <label className="form-label-modern">
                                      <i className="pi pi-tag me-2"></i>
                                      Credit Period
                                    </label>
                                    {viewMode ? (
                                      <div className="form-control-plaintext fw-semibold">{form.creditPeriod ?? 0}
                                      </div>
                                    ) : (<InputNumber value={form.creditPeriod ?? 0} onValueChange={(e) =>
                                          setForm(prev => ({...prev,creditPeriod: e.value ?? 0 }))}
                                        className="w-100 input-modern"
                                        placeholder="Enter Credit Period Value"
                                      />
                                    )}
                                  </div>
                                  {/* Currency */}
                                  <div className="form-field-group mb-3">
                                    <label className="form-label-modern">
                                      <i className="pi pi-dollar me-2"></i>
                                      Currency *
                                    </label>
                                    {viewMode ? (
                                      <div className="form-control-plaintext fw-semibold">
                                        {currencies.find(
                                          (c) => c.id === form.currencyId
                                        )?.code || "-"}
                                      </div>
                                    ) : (
                                      <Dropdown
                                        value={form.currencyId}
                                        options={currencies.map((c) => ({
                                          label: c.code ?? String(c.id),
                                          value: c.id,
                                        }))}
                                        onChange={(e) =>
                                          onChangeField("currencyId", e.value)
                                        }
                                        placeholder={
                                          currencies.length === 0
                                            ? "Loading currencies..."
                                            : "Select Currency"
                                        }
                                        className="w-100 dropdown-modern"
                                        showClear
                                        filter
                                        filterBy="label"
                                        appendTo="self"
                                      />
                                    )}
                                  </div>

                                  {/* Show Amount */}
                                  <div className="form-field-group mb-0">
                                    <label className="form-label-modern">
                                      <i className="pi pi-eye me-2"></i>
                                      Show Amount *
                                    </label>
                                    {viewMode ? (
                                      <div className="form-control-plaintext fw-semibold">
                                        {form.isShowPrice === true
                                          ? "Yes"
                                          : form.isShowPrice === false
                                          ? "No"
                                          : "-"}
                                      </div>
                                    ) : (
                                      <>
                                        <div className="radio-group-modern">
                                          <div className="d-flex align-items-center">
                                            <RadioButton
                                              inputId="showAmountYes"
                                              name="showAmount"
                                              value={true}
                                              onChange={(e) =>
                                                onChangeField(
                                                  "isShowPrice",
                                                  Boolean(e.value)
                                                )
                                              }
                                              checked={
                                                form.isShowPrice === true
                                              }
                                            />
                                            <label
                                              htmlFor="showAmountYes"
                                              className="ms-2 mb-0"
                                            >
                                              Yes
                                            </label>
                                          </div>
                                          <div className="d-flex align-items-center">
                                            <RadioButton
                                              inputId="showAmountNo"
                                              name="showAmount"
                                              value={false}
                                              onChange={(e) =>
                                                onChangeField(
                                                  "isShowPrice",
                                                  Boolean(e.value)
                                                )
                                              }
                                              checked={
                                                form.isShowPrice === false
                                              }
                                            />
                                            <label
                                              htmlFor="showAmountNo"
                                              className="ms-2 mb-0"
                                            >
                                              No
                                            </label>
                                          </div>
                                        </div>
                                        <small className="text-muted d-block mt-2">
                                          Select whether amounts should be
                                          displayed *
                                        </small>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 2: Property Selection & Room Management */}
                      {wizardState.activeIndex === 1 && (
                        <div
                          className={`wizard-step ${
                            isExpanded ? "room-view-expanded" : ""
                          }`}
                        >
                          {/* Removed verbose step heading to save vertical space */}

                          <div className="row">
                            {/* Left Column: Property Selection */}
                            {!isExpanded && (
                              <div className="col-lg-4 property-selection-column">
                                <div className="property-browser-card">
                                  <h6 className="property-selection-header">
                                    <i className="pi pi-home me-2"></i>
                                    Property Selection
                                  </h6>
                                  {/* Select City and Search Properties Side by Side */}
                                  <div className="row mb-3">
                                    {/* Select City Section */}
                                    <div className="col-md-6">
                                      <label className="form-label section-label-modern">
                                        <i className="pi pi-map-marker"></i>
                                        Select City
                                      </label>
                                      <div className="d-flex align-items-center gap-2 mb-2 autocomplete-modern">
                                        <AutoComplete
                                          value={cityACValue}
                                          suggestions={displayedCities}
                                          completeMethod={searchCities}
                                          field="name"
                                          onChange={(e) => {
                                            // Keep typing text without clearing
                                            setCityACValue(e.value);
                                          }}
                                          onSelect={(e) => {
                                            if (
                                              e.value &&
                                              typeof e.value === "object"
                                            ) {
                                              setSelectedCityId(e.value.id);
                                              setSelectedCityName(
                                                e.value.name || ""
                                              );
                                              setCityACValue(e.value);
                                            }
                                          }}
                                          onClear={() => {
                                            setSelectedCityId(null);
                                            setSelectedCityName("");
                                            setCityACValue("");
                                          }}
                                          onBlur={(e) => {
                                            // Don't clear input on blur - keep user's typed text
                                            // Only clear if there's an actual selection conflict
                                            if (
                                              typeof cityACValue === "string" &&
                                              cityACValue.trim()
                                            ) {
                                              // User typed something but didn't select - keep the text
                                              // They can continue typing or select from dropdown
                                              console.log(
                                                "[AutoComplete] Blur with text:",
                                                cityACValue
                                              );
                                            }
                                          }}
                                          placeholder={
                                            loadingCities
                                              ? "Loading cities..."
                                              : cities.length === 0
                                              ? "Type to search cities..."
                                              : "Type to search cities..."
                                          }
                                          className="flex-1"
                                          disabled={loadingCities}
                                          dropdown
                                          forceSelection={false}
                                          emptyMessage="No cities found"
                                          inputClassName="w-100"
                                          appendTo="self"
                                          minLength={1}
                                          autoHighlight={false}
                                        />
                                        <Button
                                          icon="pi pi-refresh"
                                          onClick={() => loadCities("")}
                                          className="city-action-btn"
                                          tooltip="Reload Cities"
                                          loading={loadingCities}
                                        />
                                      </div>
                                      <small className="text-muted">
                                        {cities.length > 0 && (
                                          <>Showing {cities.length} cities.</>
                                        )}
                                      </small>
                                    </div>

                                    {/* Search Properties Section */}
                                    <div className="col-md-6">
                                      <label className="form-label section-label-modern">
                                        <i className="pi pi-search"></i>
                                        Search Properties
                                      </label>
                                      <div className="d-flex align-items-center gap-2 mb-2 autocomplete-modern">
                                        <div className="p-inputgroup flex-1">
                                          <InputText
                                            value={propertySearchTerm}
                                            onChange={(e) =>
                                              setPropertySearchTerm(
                                                e.target.value
                                              )
                                            }
                                            placeholder="Search by name, pseudo name, or address..."
                                            disabled={!selectedCityId}
                                          />
                                          <span className="p-inputgroup-addon">
                                            <i className="pi pi-search"></i>
                                          </span>
                                        </div>
                                        {propertySearchTerm && (
                                          <Button
                                            icon="pi pi-times"
                                            className="city-action-btn"
                                            onClick={() =>
                                              setPropertySearchTerm("")
                                            }
                                            tooltip="Clear search"
                                          />
                                        )}
                                      </div>
                                      <small className="text-muted">
                                        {selectedCityId &&
                                          properties.length > 0 && (
                                            <>
                                              Showing{" "}
                                              {displayedProperties.length} of{" "}
                                              {properties.length} properties
                                              {propertySearchTerm &&
                                                " (filtered)"}
                                            </>
                                          )}
                                      </small>
                                    </div>
                                  </div>

                                  {selectedCityId == null ? (
                                    <div className="empty-state">
                                      <i className="pi pi-home"></i>
                                      <p className="mt-2">
                                        Please select a city to view properties
                                      </p>
                                    </div>
                                  ) : loadingProperties ? (
                                    <div className="empty-state">
                                      <i className="pi pi-spinner pi-spin"></i>
                                      <p className="mt-2">
                                        Loading properties...
                                      </p>
                                    </div>
                                  ) : properties.length === 0 ? (
                                    <div className="empty-state">
                                      <i className="pi pi-exclamation-circle"></i>
                                      <p className="mt-2">
                                        No properties found in this city
                                      </p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="mb-3">
                                        <label className="form-label section-label-modern">
                                          <i className="pi pi-building"></i>
                                          Available Properties
                                        </label>
                                        {/* Removed RMC override guidance banner */}
                                        <div className="property-list property-list-modern">
                                          {sortedDisplayedProperties.map(
                                            (p) => (
                                              <div
                                                key={p.id}
                                                className={`property-item ${
                                                  isPropertySelected(p.id)
                                                    ? "border-primary"
                                                    : ""
                                                }`}
                                              >
                                                <div className="d-flex gap-2 align-items-center">
                                                  <Checkbox
                                                    checked={
                                                      !!selectedProperties.find(
                                                        (sp) => sp.id === p.id
                                                      )
                                                    }
                                                    onChange={(e) => {
                                                      e.stopPropagation();
                                                      togglePropertySelect(p);
                                                    }}
                                                    inputId={`prop_${p.id}`}
                                                    disabled={viewMode}
                                                  />

                                                  <div className="property-thumbnail">
                                                    {p.propertyImages &&
                                                    p.propertyImages.length >
                                                      0 ? (
                                                      <img
                                                        src={
                                                          p.propertyImages.find(
                                                            (img) =>
                                                              img.isMainImage
                                                          )?.imageUrl ||
                                                          p.propertyImages[0]
                                                            ?.imageUrl
                                                        }
                                                        alt={p.name}
                                                        className="property-image"
                                                        onError={(e) => {
                                                          e.currentTarget.src =
                                                            "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e";
                                                          e.currentTarget.onerror =
                                                            null;
                                                        }}
                                                      />
                                                    ) : (
                                                      <div className="property-image-placeholder d-flex align-items-center justify-content-center">
                                                        <i className="pi pi-image"></i>
                                                      </div>
                                                    )}
                                                  </div>

                                                  <div className="flex-grow-1">
                                                    <h6 className="mb-1">
                                                      {p.name}
                                                    </h6>
                                                    <div className="d-flex align-items-center text-muted property-address">
                                                      <i className="pi pi-map-marker me-1"></i>
                                                      <span>
                                                        {p.address?.substring(
                                                          0,
                                                          30
                                                        )}
                                                        ...
                                                      </span>
                                                    </div>
                                                  </div>

                                                  <div className="d-flex gap-1">
                                                    <Button
                                                      icon="pi pi-eye"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePreviewProperty(
                                                          p
                                                        );
                                                      }}
                                                      className="p-button-text p-button-sm"
                                                      tooltip="Preview"
                                                    />
                                                    <Button
                                                      icon="pi pi-th-large"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleManageRooms(p);
                                                      }}
                                                      className="p-button-text p-button-sm"
                                                      tooltip="Manage Rooms"
                                                      disabled={
                                                        !isPropertySelected(
                                                          p.id
                                                        )
                                                      }
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>

                                        {/* Load More Button */}
                                        {hasMoreProperties && (
                                          <div className="text-center mt-3">
                                            <Button
                                              label={
                                                loadingProperties
                                                  ? "Loading..."
                                                  : "Load More Properties"
                                              }
                                              icon={
                                                loadingProperties
                                                  ? "pi pi-spinner pi-spin"
                                                  : "pi pi-angle-down"
                                              }
                                              onClick={handleLoadMoreProperties}
                                              disabled={
                                                loadingProperties || viewMode
                                              }
                                              className="p-button-outlined p-button-sm"
                                              size="small"
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {/* Button removed - properties auto-add when selected */}
                                    </>
                                  )}
                                  {/* Selected Cities Summary */}
                                  {citySelections.length > 0 && (
                                    <div className="mt-4 selected-properties-section">
                                      <h6 className="mb-3 section-label-modern">
                                        <i className="pi pi-check-circle me-2"></i>
                                        Selected Properties (
                                        {citySelections.reduce(
                                          (sum, cs) =>
                                            sum + cs.properties.length,
                                          0
                                        )}
                                        )
                                      </h6>
                                      <div className="selected-cities-list">
                                        {citySelections.map((cs) => (
                                          <div
                                            key={cs.cityId}
                                            className="selected-city-card"
                                          >
                                            <div className="d-flex justify-content-between align-items-center">
                                              <div className="city-info">
                                                <div className="city-name">
                                                  {cs.cityName}
                                                </div>
                                                <div className="property-count">
                                                  {cs.properties.length}{" "}
                                                  {cs.properties.length === 1
                                                    ? "property"
                                                    : "properties"}
                                                </div>
                                              </div>
                                              <div className="city-actions">
                                                <Button
                                                  icon={
                                                    viewMode
                                                      ? "pi pi-eye"
                                                      : "pi pi-pencil"
                                                  }
                                                  onClick={() => {
                                                    // Switch to this city to edit property selections
                                                    setSelectedCityId(
                                                      cs.cityId
                                                    );
                                                    // Load properties for this city
                                                    loadProperties("");
                                                  }}
                                                  className="p-button-text p-button-sm me-1 action-btn"
                                                  tooltip={
                                                    viewMode
                                                      ? "View"
                                                      : "Edit City Selection"
                                                  }
                                                />
                                                {!viewMode && (
                                                  <Button
                                                    icon="pi pi-trash"
                                                    onClick={() =>
                                                      removeCitySelection(
                                                        cs.cityId
                                                      )
                                                    }
                                                    className="p-button-text p-button-sm action-btn action-btn-delete"
                                                    tooltip="Remove"
                                                  />
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Right Column: Room Management / Room Viewing */}
                            <div className={isExpanded ? "col-12" : "col-lg-8"}>
                              <div className="mb-4">
                                {/* Show fullscreen toggle only when room management/list is active */}
                                {(showRoomList || showRoomManagement) && (
                                  <div className="d-flex justify-content-end mb-3">
                                    <Button
                                      icon={
                                        isExpanded
                                          ? "pi pi-window-minimize"
                                          : "pi pi-window-maximize"
                                      }
                                      onClick={() => setIsExpanded(!isExpanded)}
                                      className="p-button-text p-button-sm expand-toggle-btn"
                                      tooltip={
                                        isExpanded
                                          ? "Exit full screen"
                                          : "Expand to full screen"
                                      }
                                    />
                                  </div>
                                )}

                                {showRoomList ? (
                                  /* Room List View */
                                  <div className="room-list-section">
                                    {selectedPropertyForRoomView ? (
                                      <div
                                        className="room-view-wrapper"
                                        style={{
                                          maxHeight: "600px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <ProposalRoomWrapper
                                          proposalId={proposalId || 0}
                                          rmcClientMappingId={
                                            rmcClientMappingId
                                          }
                                          propertyName={
                                            selectedPropertyForRoomView.propertyName ||
                                            selectedPropertyForRoomView.name
                                          }
                                          onRoomDataUpdate={
                                            handleRoomDataUpdate
                                          }
                                          selectedPropertyId={
                                            selectedPropertyForRoomView.id!
                                          }
                                          isViewMode={viewMode}
                                          currencySymbol={
                                            selectedCurrencySymbol
                                          }
                                          onPropertyAutoSelect={() =>
                                            handlePropertyAutoSelect(
                                              selectedPropertyForRoomView.id!
                                            )
                                          }
                                          onPropertyAutoDeselect={() =>
                                            handlePropertyAutoDeselect(
                                              selectedPropertyForRoomView.id!
                                            )
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="text-center py-3 text-muted">
                                        <i
                                          className="pi pi-exclamation-circle"
                                          style={{
                                            fontSize: "1.5rem",
                                            opacity: 0.5,
                                          }}
                                        ></i>
                                        <p className="mt-2 small">
                                          Unable to load room information.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                ) : !showRoomManagement &&
                                  proposalId &&
                                  propertyMappings.length === 0 &&
                                  !viewMode &&
                                  !editMode ? (
                                  <div className="text-center py-4 text-muted">
                                    <i
                                      className="pi pi-exclamation-circle"
                                      style={{ fontSize: "2rem", opacity: 0.5 }}
                                    ></i>
                                    <p className="mt-2">
                                      No properties mapped. Please select
                                      properties first.
                                    </p>
                                  </div>
                                ) : !showRoomManagement ? (
                                  <div>
                                    {!viewMode && (
                                      <div className="alert alert-info mb-3">
                                        <h6 className="alert-heading mb-1">
                                          <i className="pi pi-info-circle me-1"></i>
                                          Room Management
                                        </h6>
                                        <small>
                                          Select a property to manage its rooms,
                                          charges, and complementary services.
                                        </small>
                                      </div>
                                    )}

                                    <div className="property-mappings-list">
                                      <div className="row">
                                        {propertyMappings.map((mapping) => {
                                          const property = [
                                            ...citySelections.flatMap(
                                              (cs) => cs.properties
                                            ),
                                          ].find(
                                            (p) => p.id === mapping.propertyId
                                          );
                                          const cityName =
                                            cities.find(
                                              (c) => c.id === mapping.cityId
                                            )?.name || `City ${mapping.cityId}`;

                                          if (!property) return null;

                                          return (
                                            <div
                                              key={`${mapping.cityId}-${mapping.propertyId}`}
                                              className="col-12 mb-2"
                                            >
                                              <div className="border rounded p-2">
                                                <div className="d-flex gap-2 align-items-center">
                                                  <div className="property-thumbnail">
                                                    {property.propertyImages &&
                                                    property.propertyImages
                                                      .length > 0 ? (
                                                      <img
                                                        src={
                                                          property.propertyImages.find(
                                                            (img) =>
                                                              img.isMainImage
                                                          )?.imageUrl ||
                                                          property
                                                            .propertyImages[0]
                                                            ?.imageUrl
                                                        }
                                                        alt={property.name}
                                                        style={{
                                                          width: "40px",
                                                          height: "40px",
                                                          objectFit: "cover",
                                                          borderRadius: "4px",
                                                        }}
                                                        onError={(e) => {
                                                          e.currentTarget.src =
                                                            "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e";
                                                          e.currentTarget.onerror =
                                                            null;
                                                        }}
                                                      />
                                                    ) : (
                                                      <div
                                                        style={{
                                                          width: "40px",
                                                          height: "40px",
                                                          backgroundColor:
                                                            "#f8f9fa",
                                                          borderRadius: "4px",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent:
                                                            "center",
                                                        }}
                                                      >
                                                        <i
                                                          className="pi pi-image"
                                                          style={{
                                                            fontSize: "0.8rem",
                                                            color: "#6c757d",
                                                          }}
                                                        ></i>
                                                      </div>
                                                    )}
                                                  </div>

                                                  <div className="flex-grow-1">
                                                    <div className="small fw-bold">
                                                      {property.propertyName ||
                                                        property.name}
                                                    </div>
                                                    <div
                                                      className="text-muted"
                                                      style={{
                                                        fontSize: "0.75rem",
                                                      }}
                                                    >
                                                      <i className="pi pi-map-marker me-1"></i>
                                                      {cityName}
                                                    </div>
                                                  </div>

                                                  <Button
                                                    label="Manage Rooms"
                                                    icon="pi pi-cog"
                                                    onClick={() =>
                                                      handleManageRooms(
                                                        property
                                                      )
                                                    }
                                                    className="p-button-sm"
                                                    size="small"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="room-management-section">
                                    {selectedPropertyForRooms ? (
                                      <div
                                        className="room-management-wrapper"
                                        style={{
                                          maxHeight: "600px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        <ProposalRoomWrapper
                                          key={selectedPropertyForRooms.id}
                                          proposalId={proposalId || 0}
                                          rmcClientMappingId={
                                            rmcClientMappingId
                                          }
                                          propertyName={
                                            selectedPropertyForRooms.name
                                          }
                                          onRoomDataUpdate={
                                            handleRoomDataUpdate
                                          }
                                          selectedPropertyId={
                                            selectedPropertyForRooms.id!
                                          }
                                          isViewMode={viewMode}
                                          initialRoomData={(() => {
                                            const propertyId =
                                              selectedPropertyForRooms.id!;
                                            const draft =
                                              roomDraftsRef.current[propertyId];
                                            const stored =
                                              propertyRoomData[propertyId];
                                            const data = draft || stored;

                                            console.log(
                                              `[initialRoomData] For property ${propertyId}:`,
                                              {
                                                hasDraft: !!draft,
                                                hasStored: !!stored,
                                                draftRooms:
                                                  draft?.selectedRooms || [],
                                                storedRooms:
                                                  stored?.selectedRooms || [],
                                              }
                                            );

                                            // Deep clone to prevent reference sharing between properties
                                            if (data) {
                                              const clonedData = {
                                                selectedRooms: Array.isArray(
                                                  data.selectedRooms
                                                )
                                                  ? [...data.selectedRooms]
                                                  : [],
                                                roomCharges: data.roomCharges
                                                  ? JSON.parse(
                                                      JSON.stringify(
                                                        data.roomCharges
                                                      )
                                                    )
                                                  : {},
                                                roomComplementaries:
                                                  data.roomComplementaries
                                                    ? JSON.parse(
                                                        JSON.stringify(
                                                          data.roomComplementaries
                                                        )
                                                      )
                                                    : {},
                                              };

                                              console.log(
                                                `[initialRoomData] Passing ${clonedData.selectedRooms.length} rooms to property ${propertyId}:`,
                                                clonedData.selectedRooms
                                              );

                                              return clonedData;
                                            }

                                            console.log(
                                              `[initialRoomData] No data for property ${propertyId}, returning undefined`
                                            );
                                            return undefined;
                                          })()}
                                          currencySymbol={
                                            selectedCurrencySymbol
                                          }
                                          onPropertyAutoSelect={() =>
                                            handlePropertyAutoSelect(
                                              selectedPropertyForRooms.id!
                                            )
                                          }
                                          onPropertyAutoDeselect={() =>
                                            handlePropertyAutoDeselect(
                                              selectedPropertyForRooms.id!
                                            )
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="text-center py-3 text-muted">
                                        <i
                                          className="pi pi-exclamation-circle"
                                          style={{
                                            fontSize: "1.5rem",
                                            opacity: 0.5,
                                          }}
                                        ></i>
                                        <p className="mt-2 small">
                                          Unable to load room management.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 3: Terms & Conditions */}
                      {wizardState.activeIndex === 2 && (
                        <div className="wizard-step">
                          <div className="row justify-content-center">
                            <div className="col-lg-10">
                              <div className="card shadow-sm terms-step-card">
                                <div className="card-header text-white terms-step-header">
                                  <h5 className="mb-0 d-flex align-items-center">
                                    <i className="pi pi-file-check me-2"></i>
                                    Terms & Conditions
                                  </h5>
                                </div>
                                <div className="card-body terms-step-body">
                                  <p className="text-muted terms-step-description">
                                    Enter custom terms and conditions for each
                                    property. These will appear in addition to
                                    default terms on the Property Details page.
                                  </p>

                                  {selectedProperties.length === 0 ? (
                                    <div className="alert d-flex align-items-center terms-info-alert">
                                      <i className="pi pi-info-circle me-2"></i>
                                      <span>
                                        No properties selected. Please go back
                                        to Step 2 to select properties.
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="row g-3">
                                      {selectedProperties.map((property) => (
                                        <div
                                          key={property.id}
                                          className="col-12"
                                        >
                                          <div className="card border terms-property-card">
                                            <div className="card-header d-flex justify-content-between align-items-start terms-property-header">
                                              <div>
                                                <h6 className="mb-0 fw-semibold terms-property-title">
                                                  {property.name}
                                                </h6>
                                                <small className="text-muted d-flex align-items-center terms-property-location">
                                                  <i className="pi pi-map-marker me-1"></i>
                                                  {getCityName(property)}
                                                </small>
                                              </div>
                                            </div>
                                            <div className="card-body terms-property-body">
                                              <label className="form-label fw-semibold d-flex align-items-center terms-label">
                                                <i className="pi pi-file-edit me-2"></i>
                                                Property-Specific Terms
                                              </label>
                                              {viewMode ? (
                                                <div
                                                  className="form-control-plaintext border rounded p-3"
                                                  dangerouslySetInnerHTML={{
                                                    __html:
                                                      propertyTerms[
                                                        property.id!
                                                      ] ||
                                                      "<em class='text-muted'>No terms specified</em>",
                                                  }}
                                                />
                                              ) : (
                                                <>
                                                  <Editor
                                                    value={
                                                      propertyTerms[
                                                        property.id!
                                                      ] || ""
                                                    }
                                                    onTextChange={(e: {
                                                      htmlValue: string | null;
                                                    }) => {
                                                      const propertyId =
                                                        property.id!;
                                                      const htmlValue =
                                                        e.htmlValue || "";
                                                      const htmlLength =
                                                        htmlValue.length;

                                                      // Update character count
                                                      setPropertyTermsCharCount(
                                                        (prev) => ({
                                                          ...prev,
                                                          [propertyId]:
                                                            htmlLength,
                                                        })
                                                      );

                                                      // Only update if within limit
                                                      if (
                                                        htmlLength <=
                                                        MAX_TERMS_LENGTH
                                                      ) {
                                                        setPropertyTerms(
                                                          (prev) => ({
                                                            ...prev,
                                                            [propertyId]:
                                                              htmlValue,
                                                          })
                                                        );
                                                      }
                                                    }}
                                                    style={{ height: "200px" }}
                                                    className="w-100 terms-editor"
                                                    placeholder="Enter property-specific terms and conditions..."
                                                  />
                                                  <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <small className="text-muted terms-help-text">
                                                      <i className="pi pi-info-circle me-1"></i>
                                                      Leave blank if no
                                                      additional terms are
                                                      required.
                                                    </small>
                                                    <small
                                                      className={`fw-semibold ${
                                                        (propertyTermsCharCount[
                                                          property.id!
                                                        ] || 0) >
                                                        MAX_TERMS_LENGTH
                                                          ? "text-danger"
                                                          : (propertyTermsCharCount[
                                                              property.id!
                                                            ] || 0) >
                                                            MAX_TERMS_LENGTH *
                                                              0.9
                                                          ? "text-warning"
                                                          : "text-muted"
                                                      }`}
                                                    >
                                                      {propertyTermsCharCount[
                                                        property.id!
                                                      ] || 0}{" "}
                                                      / {MAX_TERMS_LENGTH}{" "}
                                                      characters
                                                      {(propertyTermsCharCount[
                                                        property.id!
                                                      ] || 0) >
                                                        MAX_TERMS_LENGTH && (
                                                        <span className="ms-2">
                                                          <i className="pi pi-exclamation-triangle"></i>{" "}
                                                          Exceeds limit!
                                                        </span>
                                                      )}
                                                    </small>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Step 4: Preview & Submit */}
                      {wizardState.activeIndex === 3 && (
                        <div className="wizard-step">
                          <ProposalPreview
                            proposalName={form.name}
                            proposalDescription={form.description}
                            referenceCode={form.referenceCode}
                            startDate={form.startDate}
                            endDate={form.endDate}
                            currencySymbol={
                              currencies.find((c) => c.id === form.currencyId)
                                ?.symbol || "$"
                            }
                            isShowPrice={form.isShowPrice}
                            rmcName={rmcId ? `RMC ${rmcId}` : undefined}
                            clientName={clientName}
                            selectedProperties={selectedProperties}
                            propertyRoomData={propertyRoomData}
                            propertyTerms={propertyTerms}
                            cityNames={cities.reduce((acc, city) => {
                              acc[city.id] = city.name;
                              return acc;
                            }, {} as Record<number, string>)}
                            viewMode={viewMode}
                          />
                        </div>
                      )}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="wizard-navigation">
                      <div>
                        {wizardState.activeIndex > 0 && (
                          <Button
                            label="Previous"
                            icon="pi pi-chevron-left"
                            onClick={prevStep}
                            className="p-button-outlined p-button-secondary"
                          />
                        )}
                      </div>
                      <div className="d-flex gap-2">
                        {!viewMode && (
                          <Button
                            label="Cancel"
                            onClick={() =>
                              navigate(
                                rmcId ? `/rmc/${rmcId}/clients` : "/clients"
                              )
                            }
                            className="p-button-outlined p-button-secondary"
                          />
                        )}
                        {/* Save as Draft: visible in Create mode OR when editing a Draft proposal */}
                        {!viewMode && (!editMode || isDraftProposal) && (
                          <Button
                            label="Save as Draft"
                            icon="pi pi-save"
                            className="p-button-outlined"
                            onClick={async () => {
                              const error = validate();
                              if (error) {
                                Swal.fire({ icon: "warning", title: error });
                                return;
                              }
                              try {
                                let saved = false;

                                // If editing an existing draft, UPDATE it; otherwise CREATE new
                                if (proposalId && isDraftProposal) {
                                  // Update existing draft with status = 0 (Draft)
                                  saved = await updateProposalFull(0);
                                } else {
                                  // Create new draft with status = 0 (Draft)
                                  saved = await createProposalFull(0);
                                }

                                if (saved) {
                                  Swal.fire({
                                    icon: "success",
                                    title: "Proposal saved as Draft",
                                    timer: 2000,
                                    toast: true,
                                    position: "bottom",
                                    showConfirmButton: false,
                                  });
                                  // navigate back to proposals list after draft save
                                  navigate(
                                    `/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`,
                                    {
                                      state: {
                                        rmcId,
                                        clientId,
                                        clientName,
                                        rmcClientMappingId,
                                      },
                                    }
                                  );
                                }
                              } catch (err: any) {
                                console.error("Failed to save draft:", err);
                                Swal.fire({
                                  icon: "error",
                                  title: err?.message || "Failed to save draft",
                                });
                              }
                            }}
                          />
                        )}
                        {wizardState.activeIndex < wizardSteps.length - 1 ? (
                          <Button
                            label="Next"
                            icon="pi pi-chevron-right"
                            iconPos="right"
                            onClick={nextStep}
                          />
                        ) : (
                          !viewMode && (
                            <Button
                              label={
                                isDraftProposal
                                  ? "Complete Proposal"
                                  : isProposedProposal
                                  ? "Update Proposal"
                                  : "Close"
                              }
                              icon={
                                isDraftProposal || isProposedProposal
                                  ? "pi pi-check"
                                  : "pi pi-times"
                              }
                              onClick={async () => {
                                if (isDraftProposal) {
                                  // Complete the draft proposal (change status to Proposed)
                                  await submitProposal();
                                } else if (isProposedProposal) {
                                  // Update the proposed proposal (keep status as Proposed)
                                  await submitProposal();
                                } else {
                                  // Approved/Expired: Just close and go back to grid (no API calls)
                                  navigate(
                                    `/rmc/${rmcId}/clients/${rmcClientMappingId}/proposal`,
                                    {
                                      state: {
                                        rmcId,
                                        clientId,
                                        clientName,
                                        rmcClientMappingId,
                                      },
                                    }
                                  );
                                }
                              }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Property Detail Preview Modal */}
      <Modal
        show={showPropertyPreview}
        onHide={() => setShowPropertyPreview(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Property Details - {previewProperty?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewProperty && (
            <div className="row">
              <div className="col-md-6">
                {previewProperty.propertyImages &&
                previewProperty.propertyImages.length > 0 ? (
                  <Carousel interval={3000}>
                    {previewProperty.propertyImages.map((img, i) => (
                      <Carousel.Item key={i}>
                        <div
                          style={{
                            width: "100%",
                            height: 300,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8f9fa",
                          }}
                        >
                          <img
                            src={img.imageUrl}
                            alt={`${previewProperty.name} ${i + 1}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml,%3csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100' height='100' fill='%23f8f9fa'/%3e%3ctext x='50%25' y='50%25' font-size='14' text-anchor='middle' dy='.3em' fill='%23999'%3eNo Image%3c/text%3e%3c/svg%3e";
                              e.currentTarget.onerror = null;
                            }}
                          />
                        </div>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      height: 300,
                      background: "#f8f9fa",
                      borderRadius: "8px",
                    }}
                  >
                    <div className="text-center text-muted">
                      <i
                        className="pi pi-image"
                        style={{ fontSize: "3rem" }}
                      ></i>
                      <p className="mt-2">No images available</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div className="property-details-modal">
                  <h5 className="mb-3">
                    {previewProperty.propertyName || previewProperty.name}
                  </h5>
                  <div className="mb-3">
                    <h6>
                      <i className="pi pi-map-marker me-2"></i>Location
                    </h6>
                    <p className="mb-1">{previewProperty.address}</p>
                    {previewProperty.landmark && (
                      <p className="text-muted small">
                        Near {previewProperty.landmark}
                      </p>
                    )}
                    {previewProperty.zipCode && (
                      <p className="text-muted small">
                        Zip: {previewProperty.zipCode}
                      </p>
                    )}
                  </div>

                  <div className="mb-3">
                    <h6>
                      <i className="pi pi-info-circle me-2"></i>Property Info
                    </h6>
                    <div className="row">
                      <div className="col-6">
                        <small className="text-muted">Type:</small>
                        <p className="mb-1">
                          {previewProperty.propertyType?.name || "N/A"}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Total Units:</small>
                        <p className="mb-1">
                          {previewProperty.totalInventory || "N/A"}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Furnishing:</small>
                        <p className="mb-1">
                          {previewProperty.furnishingType || "N/A"}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Built Year:</small>
                        <p className="mb-1">
                          {previewProperty.buildYear || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {previewProperty.description && (
                    <div className="mb-3">
                      <h6>
                        <i className="pi pi-file-text me-2"></i>Description
                      </h6>
                      <p className="text-muted">
                        {previewProperty.description}
                      </p>
                    </div>
                  )}

                  {previewProperty.amenities &&
                    previewProperty.amenities.length > 0 && (
                      <div className="mb-3">
                        <h6>
                          <i className="pi pi-star me-2"></i>Amenities
                        </h6>
                        <div className="d-flex flex-wrap gap-1">
                          {previewProperty.amenities
                            .slice(0, 6)
                            .map((amenity, index) => (
                              <span
                                key={index}
                                className="badge bg-light text-dark"
                              >
                                {amenity.name}
                              </span>
                            ))}
                          {previewProperty.amenities.length > 6 && (
                            <span className="badge bg-secondary">
                              +{previewProperty.amenities.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                  {previewProperty.roomTypes &&
                    previewProperty.roomTypes.length > 0 && (
                      <div className="mb-3">
                        <h6>
                          <i className="pi pi-th-large me-2"></i>Room Types
                        </h6>
                        <div className="d-flex flex-wrap gap-1">
                          {previewProperty.roomTypes.map((roomType, index) => (
                            <span key={index} className="badge bg-primary">
                              {roomType.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      label="Select Property"
                      icon="pi pi-check"
                      onClick={() => {
                        if (!isPropertySelected(previewProperty.id)) {
                          togglePropertySelect(previewProperty);
                        }
                        setShowPropertyPreview(false);
                      }}
                      disabled={isPropertySelected(previewProperty.id)}
                      className="p-button-sm"
                    />
                    {previewProperty.website && (
                      <Button
                        label="Visit Website"
                        icon="pi pi-external-link"
                        onClick={() =>
                          window.open(previewProperty.website, "_blank")
                        }
                        className="p-button-outlined p-button-sm"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowPropertyPreview(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .wizard-container .p-steps {
          background: transparent;
        }

        .wizard-step {
          min-height: 400px;
        }

        .property-item {
          transition: all 0.2s ease;
          border: 1px solid #e9ecef !important;
        }

        .property-item:hover {
          border-color: var(--primary-color) !important;
          box-shadow: 0 4px 8px rgba(0,0,0,0.12);
          transform: translateY(-1px);
        }

        .property-item.border-primary {
          border-color: var(--primary-color) !important;
          box-shadow: 0 2px 8px rgba(var(--primary-color-rgb), 0.2);
        }

        .property-item .p-checkbox {
          margin-top: 2px;
        }

        .property-item .p-checkbox .p-checkbox-box {
          border-radius: 4px;
          width: 18px;
          height: 18px;
        }

        .property-details h6 {
          color: #2c3e50;
          font-size: 1.1rem;
        }

        .property-info {
          line-height: 1.4;
        }

        .property-info .pi {
          width: 16px;
          font-size: 0.85rem;
        }

        .property-image {
          transition: transform 0.2s ease;
          position: relative;
        }

        .property-image:hover {
          transform: scale(1.05);
        }

        .property-image:hover::after {
          content: '👁️ View Details';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          white-space: nowrap;
          pointer-events: none;
        }

        .property-image-placeholder {
          transition: background-color 0.2s ease;
        }

        .property-item:hover .property-image-placeholder {
          background-color: #e9ecef !important;
        }

        .badge {
          font-size: 0.7rem;
          padding: 0.3em 0.5em;
        }

        .badge .pi {
          font-size: 0.6rem;
        }

        .mapped-property-card {
          transition: all 0.2s ease;
          border: 1px solid #e9ecef !important;
        }

        .mapped-property-card:hover {
          border-color: var(--primary-color) !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .room-management-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
        }

        .p-dropdown-panel {
          z-index: 9999 !important;
          max-height: 200px !important;
        }
        .p-dropdown-items {
          max-height: 180px !important;
        }
        /* Ensure AutoComplete overlay positions tightly under input and appears above other elements */
        .p-autocomplete-panel {
          z-index: 9999 !important;
          max-height: 240px !important;
          margin-top: 4px !important;
        }
        .p-autocomplete-items {
          max-height: 220px !important;
        }
        .p-datepicker {
          z-index: 9999 !important;
        }
        .p-calendar-panel {
          z-index: 9999 !important;
        }
        .p-calendar .p-inputtext {
          cursor: pointer;
        }
      `}</style>
    </>
  );
};

export default ProposalWizard;
